/*
  نظام متابعة الدوام والإجازات
  مراقبة التشغيل - محطة الصبية

  Developed by: Abdullah Faleh Aljber
  © 2026 Abdullah Faleh Aljber. All rights reserved.

  Unauthorized copying, distribution, modification, or use of this system
  without written permission is prohibited.
*/

// src/App.js
import React, { useEffect, useState } from 'react';
import { LogOut, Plus, Printer, RefreshCw } from 'lucide-react';
import { printOfficialForm } from './officialForms';
import OfficialHeader from './OfficialHeader';
import { UserManagement, ChangePasswordButton } from './accounts';

const API_URL = window.location.port === '3000' ? 'http://localhost:5000/api' : '/api';
const APP_VERSION = '19.51.0';
const CLIENT_REFRESH_KEY = 'opd_client_refresh_key';


async function apiFetch(url, options = {}) {
  const token = localStorage.getItem('opd_token') || '';
  const headers = {
    'Content-Type': 'application/json',
    ...(options.headers || {})
  };

  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(url, {
    ...options,
    headers
  });

  if (res.status === 401) {
    let data = {};
    try { data = await res.clone().json(); } catch {}
    if (data.code === 'SESSION_REFRESH_REQUIRED' || data.code === 'TOKEN_EXPIRED') {
      localStorage.removeItem('opd_token');
      alert(data.error || 'تم تحديث النظام. يرجى تسجيل الدخول مرة أخرى.');
      window.location.reload();
    }
  }

  return res;
}

async function clearClientCaches() {
  try {
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map(registration => registration.unregister()));
    }
  } catch {}
  try {
    if ('caches' in window) {
      const keys = await window.caches.keys();
      await Promise.all(keys.map(key => window.caches.delete(key)));
    }
  } catch {}
}

async function hardReloadClient() {
  await clearClientCaches();
  const url = new URL(window.location.href);
  url.searchParams.set('_v', `${Date.now()}`);
  window.location.replace(url.toString());
}

const ROLE_LABELS_SHORT = { admin: 'مراقب النظام', manager: 'مراقب', editor: 'محرّر', viewer: 'مشاهد', employee: 'موظف', shiftSupervisor: 'مسؤول نوبة' };

const AUDIT_ACTION_LABELS = {
  create_employee: 'إضافة موظف',
  update_employee: 'تعديل موظف',
  archive_employee: 'إزالة وأرشفة موظف',
  restore_employee: 'استرجاع موظف',
  create_leave: 'طلب إجازة',
  delete_leave: 'حذف إجازة / موقف',
  approve_leave: 'اعتماد إجازة',
  reject_leave: 'رفض إجازة',
  extend_leave: 'طلب / تنفيذ تمديد',
  change_shift: 'تغيير نوبة',
  create_official_holiday: 'إضافة إجازة رسمية',
  delete_official_holiday: 'حذف إجازة رسمية',
  create_shift_system: 'تعديل إعدادات النوبات',
  create_user: 'إضافة مستخدم',
  update_user: 'تعديل مستخدم',
  activate_employee_account: 'تفعيل حساب موظف',
  submit_employee_request: 'تقديم طلب موظف',
  route_morning_request_to_manager: 'تحويل طلب صباحي للمحرر/المراقب',
  approve_by_shift_supervisor: 'اعتماد مسؤول النوبة',
  reject_by_shift_supervisor: 'رفض مسؤول النوبة',
  editor_send_to_manager: 'إرسال المحرر للمراقب',
  approve_by_manager_final: 'اعتماد المراقب النهائي',
  reject_by_manager_final: 'رفض المراقب النهائي',
  cancel_employee_request: 'إلغاء طلب موظف',
  editor_submit_leave_to_manager: 'إرسال طلب المحرر للمراقب',
  update_my_profile: 'تعديل بياناتي',
  undo_last_decision: 'تراجع عن آخر قرار',
  force_client_refresh: 'إرسال إشعار تحديث النظام للمستخدمين',
  edit_request_dates: 'تعديل تواريخ طلب',
  change_own_password: 'تغيير المستخدم لكلمة مروره',
  reset_user_password: 'إعادة تعيين كلمة مرور مستخدم',
  editor_bypass_shift_supervisor: 'تجاوز المحرر لمرحلة مسؤول النوبة',
  editor_bypass_shift_supervisor_to_manager: 'تجاوز المحرر لمسؤول النوبة وإرسال الطلب للمراقب'
};


function normalizeLeaveType(type) {
  const value = String(type || '').trim();
  const lower = value.toLowerCase();
  if (['vacation', 'annual', 'annualleave', 'leave', 'إجازة دورية', 'اجازة دورية', 'دورية'].includes(lower) || value === 'إجازة دورية') return 'vacation';
  if (['emergency', 'emergencyleave', 'عارضة', 'إجازة عارضة', 'اجازة عارضة'].includes(lower) || value === 'إجازة عارضة') return 'emergency';
  if (['extension', 'extend', 'تمديد', 'تمديد إجازة', 'تمديد اجازة'].includes(lower) || value === 'تمديد إجازة') return 'extension';
  if (['returntowork', 'return_to_work', 'return-to-work', 'return', 'مباشرة', 'مباشرة عمل'].includes(lower) || value === 'مباشرة عمل') return 'returnToWork';
  if (['sick', 'sickleave', 'مرضية', 'إجازة مرضية', 'اجازة مرضية'].includes(lower) || value === 'إجازة مرضية') return 'sick';
  return value || lower;
}

const TYPE_LABELS = {
  vacation: 'إجازة دورية',
  emergency: 'إجازة عارضة',
  sick: 'إجازة مرضية',
  absence: 'غياب',
  mission: 'مهمة رسمية',
  training: 'دورة تدريبية',
  rest: 'راحة',
  unpaid: 'إجازة بدون راتب',
  assignment: 'تكليف',
  extension: 'تمديد إجازة',
  returnToWork: 'مباشرة عمل',
};

const SHIFT_OPTIONS = [
  { value: 'A', label: 'نوبة A' },
  { value: 'B', label: 'نوبة B' },
  { value: 'C', label: 'نوبة C' },
  { value: 'D', label: 'نوبة D' },
  { value: 'F', label: 'نوبة F' },
  { value: 'M', label: 'صباحي - M' },
];

const WORK_LOCATIONS = [
  'TCR1',
  'TCR2',
  'TCR3',
  'TCR4',
  'ECR',
  'ACR',
  'Distillation',
  'SWGT(08)',
  'SWGT(24)',
  'SWGT(CCGT-3)',
  'SBGT(LM6000)',
  'SBGT(FRAME 7)',
  'SWPS',
  'صباحي'
];

const inputStyle = {
  width: '100%',
  padding: '10px',
  marginBottom: '10px',
  border: '1px solid #ddd',
  borderRadius: '4px',
  boxSizing: 'border-box'
};

const dateInputStyle = {
  ...inputStyle,
  width: '100%',
  maxWidth: 220,
  minWidth: 150,
  marginBottom: 0,
  direction: 'ltr',
  unicodeBidi: 'plaintext',
  textAlign: 'center',
  fontFamily: 'Arial, Tahoma, sans-serif',
  fontVariantNumeric: 'tabular-nums'
};

const btn = {
  padding: '10px 18px',
  border: 'none',
  borderRadius: '5px',
  cursor: 'pointer',
  color: 'white',
  fontWeight: 'bold'
};

const th = {
  padding: '8px 6px',
  textAlign: 'center',
  border: '1px solid #1d3557',
  backgroundColor: '#fff',
  color: '#0b1f4d',
  fontWeight: 700,
  verticalAlign: 'middle',
  lineHeight: 1.35
};

const td = {
  padding: '8px 6px',
  textAlign: 'center',
  border: '1px solid #1d3557',
  backgroundColor: '#fff',
  verticalAlign: 'middle',
  lineHeight: 1.4
};

function formatDateLocal(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function returnDate(endDate) {
  if (!endDate) return '';
  const d = new Date(`${endDate}T12:00:00`);
  if (Number.isNaN(d.getTime())) return '';
  d.setDate(d.getDate() + 1);
  return formatDateLocal(d);
}

function approvalStatusLabel(row) {
  const main = row.approvalStatus === 'pending' ? 'بانتظار الاعتماد'
    : row.approvalStatus === 'approved' ? 'تم الاعتماد'
    : row.approvalStatus === 'rejected' ? 'مرفوضة'
    : 'لا يحتاج اعتماد';

  if (row.extensionApprovalStatus === 'pending') return `${main} / تمديد بانتظار الاعتماد`;
  if (row.extensionApprovalStatus === 'approved') return `${main} / التمديد معتمد`;
  if (row.extensionApprovalStatus === 'rejected') return `${main} / التمديد مرفوض`;
  return main;
}

export default function AttendanceSystem() {
  const [currentUser, setCurrentUser] = useState(null);
  const [loggedIn, setLoggedIn] = useState(false);
  const [loading, setLoading] = useState(true);

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLang, setLoginLang] = useState(localStorage.getItem('opd_lang') || 'ar');
  const [showActivation, setShowActivation] = useState(false);
  const [activationForm, setActivationForm] = useState({
    fileNo: '',
    civilID: '',
    phone: '',
    email: '',
    password: '',
    confirmPassword: ''
  });

  const role = currentUser?.role || 'viewer';
  const permissions = Array.isArray(currentUser?.permissions) ? currentUser.permissions : [role];
  const hasPerm = (...items) => items.some(item => permissions.includes(item) || role === item);
  const isAdmin = hasPerm('admin', 'manager');
  const isEditor = hasPerm('editor');
  const isSystemAdmin = hasPerm('admin');
  // إدارة بيانات الموظفين (إضافة/تعديل/أرشفة/حذف السجلات) للأدمن والمحرر فقط.
  // المراقب يحتفظ بصلاحيات العرض، إدخال الطلبات، الاعتماد، الاسترجاع، التقارير وإدارة الحسابات.
  const canEdit = isSystemAdmin || isEditor;
  const canEnterEmployeeRequest = isAdmin || isEditor;
  const canViewArchive = isAdmin || isEditor;
  const isEmployeeUser = !!currentUser?.employeeId || hasPerm('employee');
  const isShiftSupervisor = hasPerm('shiftSupervisor');
  const supervisorOnly = isShiftSupervisor && !canEnterEmployeeRequest;
  const employeeOnly = isEmployeeUser && !canEnterEmployeeRequest && !isShiftSupervisor;
  const canViewEmployeeDirectory = canEnterEmployeeRequest;
  const canViewReportsAndStatements = canEnterEmployeeRequest;

  const [activeTab, setActiveTab] = useState('search');

  const [employees, setEmployees] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [employeePage, setEmployeePage] = useState(1);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [refreshingEmployees, setRefreshingEmployees] = useState(false);
  const [lastRefreshTime, setLastRefreshTime] = useState('');
  const [refreshMessage, setRefreshMessage] = useState('');

  const [leaveRecords, setLeaveRecords] = useState([]);
  const [shiftHistory, setShiftHistory] = useState([]);

  const [showAddLeave, setShowAddLeave] = useState(false);
  const [showChangeShift, setShowChangeShift] = useState(false);
  const [showEditEmployee, setShowEditEmployee] = useState(false);
  const [showAddEmployee, setShowAddEmployee] = useState(false);
  const [showArchiveEmployee, setShowArchiveEmployee] = useState(false);

  const selectedArchived = selectedEmployee && (selectedEmployee.active === false || selectedEmployee.status === 'archived');

  const [archiveForm, setArchiveForm] = useState({
    archiveDate: '',
    archiveReason: 'انتقال',
    archiveNotes: ''
  });

  const [leaveForm, setLeaveForm] = useState({
    startDate: '',
    endDate: '',
    type: 'vacation',
    notes: ''
  });

  const [shiftForm, setShiftForm] = useState({
    newShift: 'A',
    changeDate: '',
    notes: ''
  });

  const [editEmployeeForm, setEditEmployeeForm] = useState({
    fullName: '',
    fileNo: '',
    workLocation: '',
    jobTitle: '',
    phone: '',
    notes: ''
  });

  const [employeeForm, setEmployeeForm] = useState({
    fullName: '',
    civilID: '',
    fileNo: '',
    shift: 'A',
    department: 'التشغيل',
    workLocation: '',
    jobTitle: '',
    phone: ''
  });

  const [reportData, setReportData] = useState([]);
  const [reportShift, setReportShift] = useState('all');
  const [reportLoading, setReportLoading] = useState(false);

  const [locationReport, setLocationReport] = useState([]);
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationFilters, setLocationFilters] = useState({
    workLocation: 'all',
    shift: 'all'
  });

  const [statementRows, setStatementRows] = useState([]);
  const [statementLoading, setStatementLoading] = useState(false);
  const [statementSearch, setStatementSearch] = useState('');
  const [selectedStatementIds, setSelectedStatementIds] = useState([]);
  const [statementFilters, setStatementFilters] = useState({
    approvalFromDate: '',
    approvalToDate: '',
    type: 'all'
  });

  const [returnStatementRows, setReturnStatementRows] = useState([]);
  const [returnStatementLoading, setReturnStatementLoading] = useState(false);
  const [returnStatementSearch, setReturnStatementSearch] = useState('');
  const [returnStatementFilters, setReturnStatementFilters] = useState({
    approvalFromDate: '',
    approvalToDate: '',
    type: 'all'
  });

  const [pendingAlerts, setPendingAlerts] = useState({ supervisorPending: 0, managerPending: 0 });
  const [systemVersionInfo, setSystemVersionInfo] = useState(null);
  const [updateNotice, setUpdateNotice] = useState(null);

  const [officialHolidaysVersion, setOfficialHolidaysVersion] = useState(0);
  const [shiftSystemsVersion, setShiftSystemsVersion] = useState(0);

  useEffect(() => {
    localStorage.setItem('opd_lang', loginLang);
  }, [loginLang]);

  const checkSystemVersion = async ({ silent = true } = {}) => {
    try {
      const res = await fetch(`${API_URL}/system-version?clientVersion=${encodeURIComponent(APP_VERSION)}&t=${Date.now()}`, { cache: 'no-store' });
      if (!res.ok) return null;
      const data = await res.json();
      setSystemVersionInfo(data);
      const serverVersion = String(data.version || data.currentVersion || APP_VERSION);
      const refreshKey = String(data.refreshKey || serverVersion);
      const storedKey = localStorage.getItem(CLIENT_REFRESH_KEY);
      const versionMismatch = serverVersion && serverVersion !== APP_VERSION;

      // إذا كانت نسخة الواجهة نفسها قديمة، يجب إظهار التنبيه حتى في أول زيارة
      // عندما لا يكون مفتاح التحديث محفوظًا بعد. تهيئة المفتاح بصمت مسموحة
      // فقط عندما تكون نسخة الواجهة مطابقة للخادم.
      if (versionMismatch || (refreshKey && storedKey && refreshKey !== storedKey)) {
        setUpdateNotice({
          version: serverVersion,
          refreshKey,
          messageAr: data.messageAr || 'يوجد تحديث جديد للنظام. يرجى حفظ أي بيانات قبل التحديث، ثم اضغط تحديث الآن.',
          messageEn: data.messageEn || 'A new system update is available. Please save any work before updating, then click Update Now.'
        });
      } else {
        if (!storedKey && refreshKey) localStorage.setItem(CLIENT_REFRESH_KEY, refreshKey);
        if (!silent) setUpdateNotice(null);
      }
      return data;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    checkSystemVersion();
    const timer = setInterval(() => checkSystemVersion(), 300000);
    const onFocus = () => checkSystemVersion();
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(timer);
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('opd_token');
    if (!token) { setLoading(false); return; }
    fetch(`${API_URL}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(async r => {
        if (r.ok) return r.json();
        let data = {};
        try { data = await r.json(); } catch {}
        if (data.code === 'SESSION_REFRESH_REQUIRED' || data.code === 'TOKEN_EXPIRED') {
          localStorage.removeItem('opd_token');
          alert(data.error || 'تم تحديث النظام. يرجى تسجيل الدخول مرة أخرى.');
        }
        throw new Error(data.error || 'auth failed');
      })
      .then(data => { setCurrentUser(data.user); setLoggedIn(true); })
      .catch(() => { localStorage.removeItem('opd_token'); })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (loggedIn && employeeOnly && activeTab === 'search') {
      setActiveTab('myRequests');
    }
  }, [loggedIn, employeeOnly, activeTab]);

  const fetchEmployees = async (showMessage = true) => {
    setRefreshingEmployees(true);

    if (showMessage) {
      setRefreshMessage('');
    }

    try {
      const res = await apiFetch(`${API_URL}/employees`);

      if (res.ok) {
        const data = await res.json();
        const list = data.employees || [];
        setEmployees(list);

        if (showMessage) {
          const now = new Date().toLocaleTimeString('en-GB', {
            hour: '2-digit',
            minute: '2-digit'
          });

          setLastRefreshTime(now);
          setRefreshMessage(`تم تحديث القائمة بنجاح. عدد الموظفين: ${data.count || list.length}`);
        }
      } else if (showMessage) {
        setRefreshMessage('تعذر تحديث القائمة.');
      }
    } catch {
      setEmployees([]);

      if (showMessage) {
        setRefreshMessage('تعذر الاتصال بالخادم أثناء التحديث.');
      }
    }

    setRefreshingEmployees(false);
  };

  const fetchPendingAlerts = async () => {
    try {
      const res = await apiFetch(`${API_URL}/pending-alerts`);
      if (res.ok) {
        const data = await res.json();
        setPendingAlerts({
          supervisorPending: Number(data.supervisorPending || 0),
          managerPending: Number(data.managerPending || 0)
        });
      }
    } catch {
      setPendingAlerts({ supervisorPending: 0, managerPending: 0 });
    }
  };

  const fetchLeaveRecords = async (employeeId) => {
    try {
      const res = await apiFetch(`${API_URL}/leave/${employeeId}`);
      if (res.ok) {
        const data = await res.json();
        setLeaveRecords(Array.isArray(data) ? data : []);
      } else {
        setLeaveRecords([]);
      }
    } catch {
      setLeaveRecords([]);
    }
  };

  const fetchShiftHistory = async (employeeId) => {
    try {
      const res = await apiFetch(`${API_URL}/employee/${employeeId}/shift-history`);
      if (res.ok) {
        const data = await res.json();
        setShiftHistory(Array.isArray(data) ? data : []);
      } else {
        setShiftHistory([]);
      }
    } catch {
      setShiftHistory([]);
    }
  };

  const openEmployee = (emp) => {
    setSelectedEmployee(emp);
    fetchLeaveRecords(emp.id);
    fetchShiftHistory(emp.id);
    setShowAddLeave(false);
    setShowChangeShift(false);
    setShowEditEmployee(false);
    setShowArchiveEmployee(false);
    setArchiveForm({ archiveDate: '', archiveReason: 'انتقال', archiveNotes: '' });
    setEditEmployeeForm({
      fullName: emp.fullName || '',
      fileNo: emp.fileNo || '',
      workLocation: emp.workLocation || '',
      jobTitle: emp.jobTitle || '',
      phone: emp.phone || '',
      notes: emp.notes || ''
    });
  };

  const refreshSelectedEmployee = async (employeeId = selectedEmployee?.id) => {
    if (!employeeId) return;

    try {
      const res = await apiFetch(`${API_URL}/employee/${employeeId}`);

      if (res.ok) {
        const updatedEmployee = await res.json();
        setSelectedEmployee(updatedEmployee);
        setEditEmployeeForm({
          fullName: updatedEmployee.fullName || '',
          fileNo: updatedEmployee.fileNo || '',
          workLocation: updatedEmployee.workLocation || '',
          jobTitle: updatedEmployee.jobTitle || '',
          phone: updatedEmployee.phone || '',
          notes: updatedEmployee.notes || ''
        });
      }

      await fetchLeaveRecords(employeeId);
      await fetchShiftHistory(employeeId);
    } catch {
      alert('تعذر تحديث بيانات الموظف.');
    }
  };

  const handleAuth = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      });
      const data = await res.json();
      if (res.ok) {
        localStorage.setItem('opd_token', data.token);
        setCurrentUser(data.user);
        setLoggedIn(true);
        setUsername('');
        setPassword('');
      } else {
        setLoginError(data.error || 'فشل تسجيل الدخول');
      }
    } catch {
      setLoginError('تعذر الاتصال بالخادم');
    }
    setLoading(false);
  };


  const handleActivateEmployee = async (e) => {
    e.preventDefault();
    setLoginError('');
    if (activationForm.password !== activationForm.confirmPassword) {
      setLoginError(loginLang === 'en' ? 'Passwords do not match' : 'كلمتا المرور غير متطابقتين');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/auth/activate-employee`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...activationForm, language: loginLang })
      });
      const data = await res.json();
      if (res.ok) {
        localStorage.setItem('opd_token', data.token);
        localStorage.setItem('opd_lang', loginLang);
        setCurrentUser(data.user);
        setLoggedIn(true);
        setActiveTab('profile');
        setActivationForm({ fileNo: '', civilID: '', phone: '', email: '', password: '', confirmPassword: '' });
      } else {
        setLoginError(data.error || (loginLang === 'en' ? 'Activation failed' : 'فشل تفعيل الحساب'));
      }
    } catch {
      setLoginError(loginLang === 'en' ? 'Cannot connect to server' : 'تعذر الاتصال بالخادم');
    }
    setLoading(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('opd_token');
    setCurrentUser(null);
    setLoggedIn(false);
    setSelectedEmployee(null);
    setActiveTab('profile');
    setSearchQuery('');
    setEmployeePage(1);
  };

  const handleAddLeave = async (e) => {
    e.preventDefault();
    try {
      const res = await apiFetch(`${API_URL}/leave`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...leaveForm,
          employeeId: selectedEmployee.id,
          relatedLeaveId: (leaveForm.type === 'extension' || leaveForm.type === 'returnToWork') ? (leaveForm.relatedLeaveId || getSelectedAnnualFollowupOption(leaveRecords, leaveForm.relatedLeaveId)?.id || '') : ''
        })
      });

      if (res.ok) {
        alert('تم تسجيل طلب الإجازة بنجاح.');
        setLeaveForm({ startDate: '', endDate: '', type: 'vacation', notes: '', relatedLeaveId: '' });
        setShowAddLeave(false);
        await refreshSelectedEmployee(selectedEmployee.id);
      } else {
        const err = await res.json();
        alert('تنبيه: ' + (err.error || 'تعذر الحفظ'));
      }
    } catch {
      alert('تعذر الاتصال بالخادم');
    }
  };

  const handleDeleteLeave = async (id) => {
    if (!window.confirm('هل تريد حذف هذا السجل؟')) return;
    try {
      const res = await apiFetch(`${API_URL}/leave/${id}`, { method: 'DELETE' });
      if (res.ok) await refreshSelectedEmployee(selectedEmployee.id);
    } catch {
      alert('تعذر الاتصال بالخادم');
    }
  };

  const handleExtendLeave = async (record) => {
    const newEndDate = window.prompt(
      `تمديد الإجازة (${TYPE_LABELS[record.type] || record.type})\nالنهاية الحالية: ${record.endDate}\nأدخل تاريخ النهاية الجديد بصيغة YYYY-MM-DD:`,
      record.endDate || ''
    );
    if (!newEndDate) return;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(newEndDate)) {
      alert('صيغة التاريخ غير صحيحة. استخدم YYYY-MM-DD');
      return;
    }
    try {
      const res = await apiFetch(`${API_URL}/leave/${record.id}/extend`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ newEndDate })
      });
      const data = await res.json();
      if (res.ok) {
        alert(data.pendingApproval ? 'تم إرسال طلب التمديد بانتظار اعتماد المراقب.' : 'تم تمديد الإجازة بنجاح. تاريخ مباشرة العمل تحدّث تلقائياً.');
        await refreshSelectedEmployee(selectedEmployee.id);
      } else {
        alert('تنبيه: ' + (data.error || 'تعذر التمديد'));
      }
    } catch {
      alert('تعذر الاتصال بالخادم');
    }
  };

  const handleChangeShift = async (e) => {
    e.preventDefault();
    try {
      const res = await apiFetch(`${API_URL}/employee/${selectedEmployee.id}/change-shift`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(shiftForm)
      });

      const data = await res.json();

      if (res.ok) {
        alert(data.message);
        setShiftForm({ newShift: 'A', changeDate: '', notes: '' });
        setShowChangeShift(false);
        await refreshSelectedEmployee(selectedEmployee.id);
        await fetchEmployees(false);
      } else {
        alert('تنبيه: ' + (data.error || 'تعذر تغيير النوبة'));
      }
    } catch {
      alert('تعذر الاتصال بالخادم');
    }
  };

  const handleUpdateEmployee = async (e) => {
    e.preventDefault();
    try {
      const res = await apiFetch(`${API_URL}/employee/${selectedEmployee.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editEmployeeForm)
      });

      const data = await res.json();

      if (res.ok) {
        alert('تم حفظ التعديلات بنجاح.');
        setShowEditEmployee(false);
        await refreshSelectedEmployee(selectedEmployee.id);
        await fetchEmployees(false);
      } else {
        alert(data.error || 'تعذر حفظ التعديلات');
      }
    } catch {
      alert('تعذر الاتصال بالخادم');
    }
  };


  const handleArchiveEmployee = async (e) => {
    e.preventDefault();
    if (!selectedEmployee) return;
    if (!window.confirm('هل تريد إزالة وأرشفة هذا الموظف؟ سيختفي من الموظفين الحاليين ويبقى سجله محفوظًا في الأرشيف.')) return;

    try {
      const res = await apiFetch(`${API_URL}/employee/${selectedEmployee.id}/archive`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(archiveForm)
      });
      const data = await res.json();
      if (res.ok) {
        alert(data.message || 'تمت إزالة وأرشفة الموظف.');
        setShowArchiveEmployee(false);
        setSelectedEmployee(null);
        setLeaveRecords([]);
        setShiftHistory([]);
        await fetchEmployees(true);
      } else {
        alert(data.error || 'تعذر إزالة وأرشفة الموظف');
      }
    } catch {
      alert('تعذر الاتصال بالخادم');
    }
  };

  const handleRestoreEmployee = async (employeeId) => {
    const restoreNotes = window.prompt('ملاحظات الاسترجاع (اختياري):', '') || '';
    if (!window.confirm('هل تريد استرجاع الموظف إلى الموظفين الحاليين؟')) return;

    try {
      const res = await apiFetch(`${API_URL}/employee/${employeeId}/restore`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ restoreNotes })
      });
      const data = await res.json();
      if (res.ok) {
        alert(data.message || 'تم استرجاع الموظف.');
        await fetchEmployees(false);
        if (selectedEmployee?.id === employeeId) await refreshSelectedEmployee(employeeId);
      } else {
        alert(data.error || 'تعذر الاسترجاع');
      }
    } catch {
      alert('تعذر الاتصال بالخادم');
    }
  };

  const handleAddEmployee = async (e) => {
    e.preventDefault();
    try {
      const res = await apiFetch(`${API_URL}/employees`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...employeeForm,
          shiftType: employeeForm.shift === 'M' ? 'صباحي' : 'نوبات'
        })
      });

      const data = await res.json();

      if (res.ok) {
        alert('تم إضافة الموظف بنجاح.');
        setEmployeeForm({
          fullName: '',
          civilID: '',
          fileNo: '',
          shift: 'A',
          department: 'التشغيل',
          workLocation: '',
          jobTitle: '',
          phone: ''
        });
        setShowAddEmployee(false);
        await fetchEmployees(true);
      } else {
        alert(data.error || 'تعذر إضافة الموظف');
      }
    } catch {
      alert('تعذر الاتصال بالخادم');
    }
  };

  const fetchReport = async (shift = reportShift) => {
    setReportLoading(true);
    try {
      const res = await apiFetch(`${API_URL}/report?shift=${shift}`);
      if (res.ok) {
        const data = await res.json();
        setReportData(data.report || []);
      } else {
        setReportData([]);
      }
    } catch {
      setReportData([]);
    }
    setReportLoading(false);
  };

  const fetchLocationReport = async () => {
    setLocationLoading(true);
    try {
      const params = new URLSearchParams({
        workLocation: locationFilters.workLocation,
        shift: locationFilters.shift
      });
      const res = await apiFetch(`${API_URL}/work-location-report?${params.toString()}`);
      if (res.ok) {
        const data = await res.json();
        setLocationReport(data.employees || []);
      } else {
        setLocationReport([]);
      }
    } catch {
      setLocationReport([]);
    }
    setLocationLoading(false);
  };

  const fetchLeaveStatement = async () => {
    setStatementLoading(true);
    try {
      const params = new URLSearchParams();
      if (statementFilters.approvalFromDate) params.set('approvalFromDate', statementFilters.approvalFromDate);
      if (statementFilters.approvalToDate) params.set('approvalToDate', statementFilters.approvalToDate);
      if (statementFilters.type && statementFilters.type !== 'all') params.set('type', statementFilters.type);
      if (selectedStatementIds.length > 0) params.set('employeeIds', selectedStatementIds.join(','));
      if (statementSearch.trim()) params.set('q', statementSearch.trim());

      const res = await apiFetch(`${API_URL}/leave-statement?${params.toString()}`);

      if (res.ok) {
        const data = await res.json();
        setStatementRows(data.rows || []);
      } else {
        const err = await res.json();
        alert(err.error || 'تعذر تحميل بيان الإجازات');
        setStatementRows([]);
      }
    } catch {
      alert('تعذر الاتصال بالخادم');
      setStatementRows([]);
    }
    setStatementLoading(false);
  };

  const fetchReturnStatement = async () => {
    setReturnStatementLoading(true);
    try {
      const params = new URLSearchParams();
      if (returnStatementFilters.approvalFromDate) params.set('approvalFromDate', returnStatementFilters.approvalFromDate);
      if (returnStatementFilters.approvalToDate) params.set('approvalToDate', returnStatementFilters.approvalToDate);
      if (returnStatementFilters.type && returnStatementFilters.type !== 'all') params.set('type', returnStatementFilters.type);
      if (returnStatementSearch.trim()) params.set('q', returnStatementSearch.trim());

      const res = await apiFetch(`${API_URL}/return-statement?${params.toString()}`);

      if (res.ok) {
        const data = await res.json();
        setReturnStatementRows(data.rows || []);
      } else {
        const err = await res.json();
        alert(err.error || 'تعذر تحميل بيان مباشرة العمل');
        setReturnStatementRows([]);
      }
    } catch {
      alert('تعذر الاتصال بالخادم');
      setReturnStatementRows([]);
    }
    setReturnStatementLoading(false);
  };

  const calculateStats = () => {
    const totals = {
      vacation: 0,
      emergency: 0,
      sick: 0,
      absence: 0,
      mission: 0,
      training: 0,
      rest: 0,
      unpaid: 0,
      assignment: 0
    };

    let workImpactDays = 0;

    leaveRecords.forEach(r => {
      if (totals[r.type] !== undefined) totals[r.type] += r.actualDays ?? r.days ?? 0;
      workImpactDays += r.workImpactDays ?? r.actualDays ?? r.days ?? 0;
    });

    const totalDays = Object.values(totals).reduce((a, b) => a + b, 0);
    const baseDays = selectedEmployee?.baseWorkDays || (selectedEmployee?.shift === 'M' || selectedEmployee?.shiftType === 'صباحي' ? 244 : 219);

    return { ...totals, totalDays, workImpactDays, workDays: baseDays - workImpactDays };
  };

  const filteredEmployees = employees.filter(emp =>
    String(emp.fullName || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    String(emp.civilID || '').includes(searchQuery) ||
    String(emp.fileNo || '').includes(searchQuery) ||
    String(emp.workLocation || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const employeePageSize = 25;
  const employeeTotalPages = Math.max(1, Math.ceil(filteredEmployees.length / employeePageSize));
  const safeEmployeePage = Math.min(employeePage, employeeTotalPages);
  const paginatedEmployees = filteredEmployees.slice((safeEmployeePage - 1) * employeePageSize, safeEmployeePage * employeePageSize);

  const getAllowedTabs = () => {
    if (employeeOnly) return ['profile', 'myRequests'];
    if (supervisorOnly) return ['profile', 'myRequests', 'supervisorRequests', 'onDutyToday'];
    return [
      ...(isEmployeeUser ? ['profile', 'myRequests'] : []),
      ...(canViewEmployeeDirectory ? ['search'] : []),
      ...(canViewReportsAndStatements ? ['report', 'locationReport', 'leaveStatement', 'returnStatement'] : []),
      ...(isShiftSupervisor || isSystemAdmin ? ['supervisorRequests'] : []),
      ...(isShiftSupervisor || isAdmin ? ['onDutyToday'] : []),
      ...(isAdmin ? ['managerRequests'] : []),
      ...(canEnterEmployeeRequest ? ['requestsTracking'] : []),
      ...(canEdit ? ['addEmployee'] : []),
      ...(canViewArchive ? ['archive'] : []),
      ...(isAdmin ? ['approvals', 'officialHolidays', 'auditLogs', 'systemUpdate'] : []),
      ...(isAdmin || isEditor ? ['manage'] : [])
    ];
  };

  useEffect(() => {
    if (!loggedIn || !currentUser) return;
    const allowed = getAllowedTabs();
    if (!allowed.includes(activeTab)) setActiveTab(allowed[0] || 'profile');
  }, [loggedIn, currentUser?.id, role, permissions.join(','), activeTab]);

  useEffect(() => {
    if (!loggedIn || !currentUser) return;
    fetchPendingAlerts();
  }, [loggedIn, currentUser?.id, role, permissions.join(',')]);

  const supervisorPendingCount = Number(pendingAlerts.supervisorPending || 0);
  const managerPendingCount = Number(pendingAlerts.managerPending || 0);

  const statementEmployeeList = employees
    .filter(emp =>
      String(emp.fullName || '').toLowerCase().includes(statementSearch.toLowerCase()) ||
      String(emp.civilID || '').includes(statementSearch) ||
      String(emp.fileNo || '').includes(statementSearch)
    )
    .slice(0, 100);

  const toggleStatementEmployee = (id) => {
    setSelectedStatementIds(prev =>
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  const selectAllStatementFiltered = () => {
    setSelectedStatementIds(statementEmployeeList.map(emp => emp.id));
  };

  const clearStatementSelection = () => {
    setSelectedStatementIds([]);
    setStatementRows([]);
  };

  const renderTabs = () => {
    const userLang = currentUser?.language || 'ar';
    const profileLabel = userLang === 'en' ? 'My Profile' : 'بياناتي';
    const requestsLabel = userLang === 'en' ? 'My Requests' : 'طلباتي';
    const supervisorApprovalLabel = userLang === 'en' ? 'Shift Supervisor Approval' : 'اعتماد مسؤول النوبة';
    const managerApprovalLabel = userLang === 'en' ? 'Manager Final Approval' : 'اعتماد المراقب النهائي';
    const requestsTrackingLabel = userLang === 'en' ? 'Employee Requests Tracking' : 'متابعة طلبات الموظفين';
    const systemUpdateLabel = userLang === 'en' ? 'System Update' : 'تحديث المستخدمين';
    const onDutyTodayLabel = userLang === 'en' ? 'Daily Attendance Status' : 'كشف الحضور اليومي';
    const tabItems = employeeOnly ? [
      ['profile', profileLabel],
      ['myRequests', requestsLabel]
    ] : supervisorOnly ? [
      ['profile', profileLabel],
      ['myRequests', requestsLabel],
      ['supervisorRequests', supervisorApprovalLabel],
      ['onDutyToday', onDutyTodayLabel]
    ] : [
      ...(isEmployeeUser ? [['profile', profileLabel], ['myRequests', requestsLabel]] : []),
      ...(canViewEmployeeDirectory ? [['search', 'البحث والعرض']] : []),
      ...(canViewReportsAndStatements ? [['report', 'التقرير السنوي'], ['locationReport', 'كشف أماكن العمل'], ['leaveStatement', 'بيان الإجازات'], ['returnStatement', 'بيان مباشرة العمل']] : []),
      ...(isShiftSupervisor || isSystemAdmin ? [['supervisorRequests', supervisorApprovalLabel]] : []),
      ...(isShiftSupervisor || isAdmin ? [['onDutyToday', onDutyTodayLabel]] : []),
      ...(isAdmin ? [['managerRequests', managerApprovalLabel]] : []),
      ...(canEnterEmployeeRequest ? [['requestsTracking', requestsTrackingLabel]] : []),
      ...(canEdit ? [['addEmployee', 'إضافة موظف']] : []),
      ...(canViewArchive ? [['archive', 'الأرشيف']] : []),
      ...(isAdmin ? [['approvals', 'اعتماد الإجازات القديمة']] : []),
      ...(isAdmin ? [['officialHolidays', 'الإجازات الرسمية']] : []),
      ...(isAdmin ? [['auditLogs', 'سجل العمليات']] : []),
      ...(isAdmin ? [['systemUpdate', systemUpdateLabel]] : []),
      ...(isAdmin || isEditor ? [['manage', userLang === 'en' ? 'User Passwords' : 'كلمات مرور المستخدمين']] : [])
    ];

    const labelWithPendingCount = (tab, label) => {
      const count = tab === 'supervisorRequests' ? supervisorPendingCount : tab === 'managerRequests' ? managerPendingCount : 0;
      return count > 0 ? `${label} (${count})` : label;
    };

    return (
      <div style={{ backgroundColor: 'white', borderBottom: '2px solid #ddd' }} className="no-print">
        {tabItems.map(([tab, label]) => (
          <button
            key={tab}
            onClick={() => {
              setActiveTab(tab);
              if (tab === 'report') fetchReport();
              if (tab === 'locationReport') fetchLocationReport();
              if (tab === 'leaveStatement' && employees.length === 0) fetchEmployees(false);
              if (tab === 'returnStatement' && returnStatementRows.length === 0) fetchReturnStatement();
              if (tab === 'supervisorRequests' || tab === 'managerRequests') fetchPendingAlerts();
            }}
            style={{
              padding: '15px 18px',
              border: 'none',
              backgroundColor: activeTab === tab ? '#667eea' : 'transparent',
              color: activeTab === tab ? 'white' : '#666',
              cursor: 'pointer',
              fontSize: '15px'
            }}
          >
            {labelWithPendingCount(tab, label)}
          </button>
        ))}
      </div>
    );
  };

  if (loading) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>جاري التحميل...</div>;
  }

  if (!loggedIn) {
    const isEnglish = loginLang === 'en';
    const text = {
      title: isEnglish ? 'Attendance & Leave System' : 'نظام متابعة الدوام',
      username: isEnglish ? 'File No. / Username' : 'رقم الملف / اسم المستخدم',
      password: isEnglish ? 'Password' : 'كلمة المرور',
      login: isEnglish ? 'Login' : 'تسجيل الدخول',
      firstTime: isEnglish ? 'First Time Activation' : 'تفعيل الحساب لأول مرة',
      backToLogin: isEnglish ? 'Back to Login' : 'العودة لتسجيل الدخول',
      fileNo: isEnglish ? 'File Number' : 'رقم الملف',
      civilID: isEnglish ? 'Civil ID' : 'الرقم المدني',
      phone: isEnglish ? 'Phone Number' : 'رقم الهاتف',
      email: isEnglish ? 'Email (optional)' : 'الإيميل (اختياري)',
      newPassword: isEnglish ? 'New Password' : 'كلمة مرور جديدة',
      confirmPassword: isEnglish ? 'Confirm Password' : 'تأكيد كلمة المرور',
      activate: isEnglish ? 'Activate Account' : 'تفعيل الحساب',
      hint: isEnglish ? 'Use your file number and civil ID for first activation only.' : 'استخدم رقم الملف والرقم المدني للتفعيل أول مرة فقط.'
    };
    return (
      <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }} dir={isEnglish ? 'ltr' : 'rtl'}>
        <div style={{ backgroundColor: 'white', padding: '40px', borderRadius: '12px', width: '100%', maxWidth: '440px' }}>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginBottom: 18 }}>
            <button type="button" onClick={() => setLoginLang('ar')} style={{ ...btn, backgroundColor: loginLang === 'ar' ? '#667eea' : '#95a5a6' }}>عربي</button>
            <button type="button" onClick={() => setLoginLang('en')} style={{ ...btn, backgroundColor: loginLang === 'en' ? '#667eea' : '#95a5a6' }}>English</button>
          </div>

          <h1 style={{ textAlign: 'center', marginBottom: '12px', color: '#2c3e50', fontSize: '28px' }}>
            {text.title}
          </h1>

          <div dir="ltr" style={{ textAlign: 'center', color: '#555', marginTop: 0, marginBottom: '25px', lineHeight: 1.6 }}>
            <div style={{ fontSize: '13px' }}>Developed by</div>
            <div style={{ fontSize: '17px', fontWeight: 'bold', color: '#2c3e50' }}>Abdullah Faleh Aljber</div>
            <div style={{ fontSize: '12px', color: '#777' }}>© 2026 All rights reserved.</div>
          </div>

          {!showActivation ? (
            <form onSubmit={handleAuth}>
              <input type="text" placeholder={text.username} value={username} onChange={(e) => setUsername(e.target.value)} style={{ ...inputStyle, padding: '12px', marginBottom: '15px' }} required />
              <input type="password" placeholder={text.password} value={password} onChange={(e) => setPassword(e.target.value)} style={{ ...inputStyle, padding: '12px', marginBottom: '15px' }} required />
              {loginError && <p style={{ color: '#e74c3c', fontWeight: 'bold', marginTop: 0, marginBottom: '15px', textAlign: 'center' }}>{loginError}</p>}
              <button type="submit" style={{ width: '100%', padding: '12px', backgroundColor: '#667eea', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '16px' }}>
                {text.login}
              </button>
              <button type="button" onClick={() => { setShowActivation(true); setLoginError(''); }} style={{ width: '100%', padding: '11px', marginTop: 10, backgroundColor: '#16a085', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}>
                {text.firstTime}
              </button>
            </form>
          ) : (
            <form onSubmit={handleActivateEmployee}>
              <p style={{ color: '#666', fontSize: 13, textAlign: 'center' }}>{text.hint}</p>
              <input type="text" placeholder={text.fileNo} value={activationForm.fileNo} onChange={(e) => setActivationForm({ ...activationForm, fileNo: e.target.value })} style={{ ...inputStyle, padding: '12px', marginBottom: '12px' }} required />
              <input type="text" placeholder={text.civilID} value={activationForm.civilID} onChange={(e) => setActivationForm({ ...activationForm, civilID: e.target.value })} style={{ ...inputStyle, padding: '12px', marginBottom: '12px' }} required />
              <input type="text" placeholder={text.phone} value={activationForm.phone} onChange={(e) => setActivationForm({ ...activationForm, phone: e.target.value })} style={{ ...inputStyle, padding: '12px', marginBottom: '12px' }} />
              <input type="email" placeholder={text.email} value={activationForm.email} onChange={(e) => setActivationForm({ ...activationForm, email: e.target.value })} style={{ ...inputStyle, padding: '12px', marginBottom: '12px' }} />
              <input type="password" placeholder={text.newPassword} value={activationForm.password} onChange={(e) => setActivationForm({ ...activationForm, password: e.target.value })} style={{ ...inputStyle, padding: '12px', marginBottom: '12px' }} required />
              <input type="password" placeholder={text.confirmPassword} value={activationForm.confirmPassword} onChange={(e) => setActivationForm({ ...activationForm, confirmPassword: e.target.value })} style={{ ...inputStyle, padding: '12px', marginBottom: '12px' }} required />
              {loginError && <p style={{ color: '#e74c3c', fontWeight: 'bold', marginTop: 0, marginBottom: '15px', textAlign: 'center' }}>{loginError}</p>}
              <button type="submit" style={{ width: '100%', padding: '12px', backgroundColor: '#16a085', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '16px' }}>
                {text.activate}
              </button>
              <button type="button" onClick={() => { setShowActivation(false); setLoginError(''); }} style={{ width: '100%', padding: '11px', marginTop: 10, backgroundColor: '#95a5a6', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}>
                {text.backToLogin}
              </button>
            </form>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8f9fa' }} dir={currentUser?.language === 'en' ? 'ltr' : 'rtl'}>
      <div style={{ backgroundColor: '#2c3e50', color: 'white', padding: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }} className="no-print">
        <h1 style={{ margin: 0, fontSize: '24px' }}>{currentUser?.language === 'en' ? 'Attendance System' : 'نظام متابعة الدوام'}</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <span>👤 {currentUser?.name || currentUser?.username} <small style={{ opacity: 0.8 }}>({ROLE_LABELS_SHORT[role] || role})</small></span>
          <button onClick={() => hardReloadClient()} style={{ backgroundColor: '#3498db', color: 'white', padding: '8px 12px', border: 'none', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 'bold' }}>
            <RefreshCw size={16} /> {currentUser?.language === 'en' ? 'Refresh System' : 'تحديث النظام'}
          </button>
          <ChangePasswordButton language={currentUser?.language || 'ar'} />
          <button onClick={handleLogout} style={{ backgroundColor: '#e74c3c', color: 'white', padding: '8px 16px', border: 'none', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <LogOut size={18} /> {currentUser?.language === 'en' ? 'Logout' : 'تسجيل خروج'}
          </button>
        </div>
      </div>

      {updateNotice && (
        <div className="no-print" style={{ background: '#fff3cd', color: '#7a4f01', borderBottom: '1px solid #f1c40f', padding: '10px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', fontWeight: 'bold' }}>
          <div style={{ lineHeight: 1.7 }}>
            <div>{updateNotice.messageAr || 'يوجد تحديث جديد للنظام. يرجى حفظ أي بيانات قبل التحديث، ثم اضغط تحديث الآن.'}</div>
            <div dir="ltr">{updateNotice.messageEn || 'A new system update is available. Please save any work before updating, then click Update Now.'}</div>
          </div>
          <button onClick={async () => {
            if (updateNotice?.refreshKey) localStorage.setItem(CLIENT_REFRESH_KEY, String(updateNotice.refreshKey));
            setUpdateNotice(null);
            await hardReloadClient();
          }} style={{ ...btn, backgroundColor: '#d35400', padding: '7px 12px' }}>تحديث الآن / Update Now</button>
        </div>
      )}

      {renderTabs()}

      {(supervisorPendingCount > 0 || managerPendingCount > 0) && (
        <div className="no-print" style={{ maxWidth: '1400px', margin: '16px auto 0', padding: '0 20px' }}>
          <div style={{ backgroundColor: '#fff3cd', border: '1px solid #ffeeba', color: '#856404', padding: '12px 15px', borderRadius: '8px', fontWeight: 'bold' }}>
            {supervisorPendingCount > 0 && (
              <button onClick={() => setActiveTab('supervisorRequests')} style={{ background: 'transparent', border: 'none', color: '#856404', fontWeight: 'bold', cursor: 'pointer', marginInlineEnd: 12 }}>
                ⚠️ {currentUser?.language === 'en' ? `You have ${supervisorPendingCount} shift supervisor request(s) pending.` : `لديك ${supervisorPendingCount} طلب بانتظار اعتماد مسؤول النوبة.`}
              </button>
            )}
            {managerPendingCount > 0 && (
              <button onClick={() => setActiveTab('managerRequests')} style={{ background: 'transparent', border: 'none', color: '#856404', fontWeight: 'bold', cursor: 'pointer' }}>
                ⚠️ {currentUser?.language === 'en' ? `You have ${managerPendingCount} manager approval request(s) pending.` : `لديك ${managerPendingCount} طلب بانتظار اعتماد المراقب.`}
              </button>
            )}
          </div>
        </div>
      )}

      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '30px 20px' }}>
        {activeTab === 'profile' && (
          <ProfileTab currentUser={currentUser} onUserUpdate={(u) => setCurrentUser(prev => ({ ...prev, ...u }))} />
        )}

        {activeTab === 'myRequests' && (
          <MyRequestsTab currentUser={currentUser} />
        )}

        {activeTab === 'supervisorRequests' && (
          (isShiftSupervisor || isSystemAdmin) ? <SupervisorRequestsTab currentUser={currentUser} onDecisionDone={fetchPendingAlerts} /> : null
        )}

        {activeTab === 'onDutyToday' && (
          (isShiftSupervisor || isAdmin) ? <OnDutyTodayTab currentUser={currentUser} /> : null
        )}

        {activeTab === 'managerRequests' && (
          isAdmin ? <ManagerRequestsTab currentUser={currentUser} onDecisionDone={fetchPendingAlerts} /> : null
        )}

        {activeTab === 'requestsTracking' && (
          canEnterEmployeeRequest ? <EmployeeRequestsTrackingTab currentUser={currentUser} /> : null
        )}

        {activeTab === 'search' && canViewEmployeeDirectory && (
          <>
            {!selectedEmployee ? (
              <div>
                <input
                  type="text"
                  placeholder="ابحث بالاسم أو الرقم المدني أو رقم الملف أو مكان العمل..."
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); setEmployeePage(1); }}
                  onFocus={() => { if (employees.length === 0) fetchEmployees(false); }}
                  style={{ width: '100%', padding: '12px', border: '2px solid #ddd', borderRadius: '8px', marginBottom: '20px', boxSizing: 'border-box', fontSize: '16px' }}
                />

                <div className="no-print" style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '15px', flexWrap: 'wrap' }}>
                  <button
                    onClick={() => fetchEmployees(true)}
                    disabled={refreshingEmployees}
                    style={{
                      ...btn,
                      backgroundColor: refreshingEmployees ? '#95a5a6' : '#3498db',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      opacity: refreshingEmployees ? 0.8 : 1
                    }}
                  >
                    <RefreshCw size={16} /> {refreshingEmployees ? 'جاري التحديث...' : 'تحديث القائمة'}
                  </button>
                  <p style={{ color: '#666', margin: 0 }}>عدد الموظفين: {employees.length}</p>
                  <p style={{ color: '#666', margin: 0 }}>المعروض: {filteredEmployees.length} نتيجة — الصفحة {safeEmployeePage} من {employeeTotalPages}</p>
                </div>

                {refreshMessage && (
                  <p style={{
                    color: refreshMessage.includes('بنجاح') ? '#27ae60' : '#e74c3c',
                    marginTop: '-5px',
                    marginBottom: '15px',
                    fontWeight: 'bold'
                  }}>
                    {refreshMessage}{lastRefreshTime ? ` - آخر تحديث: ${lastRefreshTime}` : ''}
                  </p>
                )}

                <div style={{ backgroundColor: 'white', borderRadius: 8, border: '1px solid #ddd', overflowX: 'auto' }}>
                  <table style={{ width: '100%', minWidth: 950, borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        {['اختيار', 'الاسم', 'الرقم المدني', 'رقم الملف', 'النوبة', 'مكان العمل', 'المسمى الوظيفي'].map(h => <th key={h} style={th}>{h}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedEmployees.length === 0 ? (
                        <tr><td colSpan="7" style={{ ...td, textAlign: 'center', color: '#777' }}>لا توجد نتائج مطابقة</td></tr>
                      ) : paginatedEmployees.map(emp => (
                        <tr key={emp.id} style={{ cursor: 'pointer' }} onDoubleClick={() => openEmployee(emp)}>
                          <td style={td}><button onClick={() => openEmployee(emp)} style={{ ...btn, backgroundColor: '#16a085', padding: '6px 12px' }}>عرض</button></td>
                          <td style={{ ...td, fontWeight: 'bold', color: '#2c3e50' }}>{emp.fullName || '-'}</td>
                          <td style={td}>{emp.civilID || '-'}</td>
                          <td style={td}>{emp.fileNo || '-'}</td>
                          <td style={td}>{emp.shift || '-'} {emp.shiftType === 'صباحي' ? '(صباحي)' : ''}</td>
                          <td style={td}>{emp.workLocation || emp.workplace || '-'}</td>
                          <td style={td}>{emp.jobTitle || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="no-print" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 16 }}>
                  <button
                    onClick={() => setEmployeePage(p => Math.max(1, p - 1))}
                    disabled={safeEmployeePage <= 1}
                    style={{ ...btn, backgroundColor: safeEmployeePage <= 1 ? '#bdc3c7' : '#34495e', padding: '7px 12px' }}
                  >السابق</button>
                  {Array.from({ length: employeeTotalPages }, (_, i) => i + 1).map(page => (
                    <button
                      key={page}
                      onClick={() => setEmployeePage(page)}
                      style={{ ...btn, backgroundColor: page === safeEmployeePage ? '#667eea' : '#95a5a6', padding: '7px 11px', minWidth: 38 }}
                    >{page}</button>
                  ))}
                  <button
                    onClick={() => setEmployeePage(p => Math.min(employeeTotalPages, p + 1))}
                    disabled={safeEmployeePage >= employeeTotalPages}
                    style={{ ...btn, backgroundColor: safeEmployeePage >= employeeTotalPages ? '#bdc3c7' : '#34495e', padding: '7px 12px' }}
                  >التالي</button>
                </div>
              </div>
            ) : (
              <div>
                <button onClick={() => setSelectedEmployee(null)} className="no-print" style={{ marginBottom: '20px', padding: '10px 20px', backgroundColor: '#95a5a6', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer' }}>
                  ← العودة
                </button>

                <div style={{ backgroundColor: 'white', padding: '30px', borderRadius: '8px', marginBottom: '20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '10px' }}>
                    <h2 style={{ margin: '0 0 20px 0', color: '#2c3e50' }}>{selectedEmployee.fullName}</h2>
                    {canEdit && !selectedArchived && (
                      <div className="no-print" style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                        <button onClick={() => setShowEditEmployee(!showEditEmployee)} style={{ ...btn, backgroundColor: '#34495e' }}>تعديل البيانات</button>
                        <button onClick={() => setShowChangeShift(!showChangeShift)} style={{ ...btn, backgroundColor: '#8e44ad', display: 'flex', alignItems: 'center', gap: '8px' }}><RefreshCw size={16} /> تغيير النوبة</button>
                        <button onClick={() => setShowArchiveEmployee(!showArchiveEmployee)} style={{ ...btn, backgroundColor: '#c0392b' }}>إزالة وأرشفة</button>
                      </div>
                    )}
                    {isAdmin && selectedArchived && (
                      <div className="no-print" style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                        <button onClick={() => handleRestoreEmployee(selectedEmployee.id)} style={{ ...btn, backgroundColor: '#27ae60' }}>استرجاع من الأرشيف</button>
                      </div>
                    )}
                  </div>

                  {selectedArchived && (
                    <div style={{ backgroundColor: '#fff3cd', color: '#856404', padding: '14px', borderRadius: '8px', marginBottom: '15px', border: '1px solid #ffeeba' }}>
                      <strong>هذا الموظف مؤرشف</strong>
                      <div style={{ fontSize: 13, marginTop: 5 }}>
                        السبب: {selectedEmployee.archiveReason || '-'} | تاريخ الإزالة: {selectedEmployee.archiveDate || '-'} | بواسطة: {selectedEmployee.archivedByName || '-'}
                      </div>
                      {selectedEmployee.archiveNotes && <div style={{ fontSize: 13, marginTop: 5 }}>ملاحظات: {selectedEmployee.archiveNotes}</div>}
                    </div>
                  )}

                  {showArchiveEmployee && !selectedArchived && (
                    <div style={{ backgroundColor: '#fff5f5', padding: '20px', borderRadius: '8px', marginBottom: '20px', border: '1px solid #f1b0b7' }} className="no-print">
                      <h3 style={{ marginTop: 0, color: '#c0392b' }}>إزالة وأرشفة الموظف</h3>
                      <p style={{ color: '#666' }}>سيختفي الموظف من عدد الموظفين الحاليين، لكن يبقى سجله القديم محفوظًا في الأرشيف.</p>
                      <form onSubmit={handleArchiveEmployee}>
                        <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px' }}>تاريخ الإزالة / الانتقال:</label>
                        <input type="date" dir="ltr" lang="en-CA" value={archiveForm.archiveDate} onChange={(e) => setArchiveForm({ ...archiveForm, archiveDate: e.target.value })} style={dateInputStyle} required />
                        <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px' }}>سبب الإزالة:</label>
                        <select value={archiveForm.archiveReason} onChange={(e) => setArchiveForm({ ...archiveForm, archiveReason: e.target.value })} style={inputStyle} required>
                          <option value="انتقال">انتقال</option>
                          <option value="استقالة">استقالة</option>
                          <option value="تقاعد">تقاعد</option>
                          <option value="إنهاء خدمة">إنهاء خدمة</option>
                          <option value="أخرى">أخرى</option>
                        </select>
                        <textarea placeholder="ملاحظات" value={archiveForm.archiveNotes} onChange={(e) => setArchiveForm({ ...archiveForm, archiveNotes: e.target.value })} style={{ ...inputStyle, minHeight: '70px' }} />
                        <button type="submit" style={{ ...btn, backgroundColor: '#c0392b', marginLeft: '10px' }}>تأكيد الإزالة والأرشفة</button>
                        <button type="button" onClick={() => setShowArchiveEmployee(false)} style={{ ...btn, backgroundColor: '#95a5a6' }}>إلغاء</button>
                      </form>
                    </div>
                  )}

                  {showEditEmployee && (
                    <div style={{ backgroundColor: '#f8f9fa', padding: '20px', borderRadius: '8px', marginBottom: '20px', border: '1px solid #ddd' }} className="no-print">
                      <h3 style={{ marginTop: 0 }}>تعديل بيانات الموظف</h3>
                      <form onSubmit={handleUpdateEmployee}>
                        <input type="text" placeholder="اسم الموظف" value={editEmployeeForm.fullName} onChange={(e) => setEditEmployeeForm({ ...editEmployeeForm, fullName: e.target.value })} style={inputStyle} required />
                        <input type="text" placeholder="رقم الملف" value={editEmployeeForm.fileNo} onChange={(e) => setEditEmployeeForm({ ...editEmployeeForm, fileNo: e.target.value })} style={inputStyle} required />
                        <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px' }}>مكان العمل:</label>
                        <select value={editEmployeeForm.workLocation} onChange={(e) => setEditEmployeeForm({ ...editEmployeeForm, workLocation: e.target.value })} style={inputStyle}>
                          <option value="">غير محدد</option>
                          {WORK_LOCATIONS.map(item => <option key={item} value={item}>{item}</option>)}
                        </select>
                        <input type="text" placeholder="المسمى الوظيفي" value={editEmployeeForm.jobTitle} onChange={(e) => setEditEmployeeForm({ ...editEmployeeForm, jobTitle: e.target.value })} style={inputStyle} />
                        <input type="text" placeholder="رقم الهاتف" value={editEmployeeForm.phone} onChange={(e) => setEditEmployeeForm({ ...editEmployeeForm, phone: e.target.value })} style={inputStyle} />
                        <input type="text" placeholder="ملاحظات دائمة" value={editEmployeeForm.notes} onChange={(e) => setEditEmployeeForm({ ...editEmployeeForm, notes: e.target.value })} style={inputStyle} />
                        <button type="submit" style={{ ...btn, backgroundColor: '#27ae60', marginLeft: '10px' }}>حفظ التعديل</button>
                        <button type="button" onClick={() => setShowEditEmployee(false)} style={{ ...btn, backgroundColor: '#95a5a6' }}>إلغاء</button>
                      </form>
                    </div>
                  )}

                  {showChangeShift && (
                    <div style={{ backgroundColor: '#f3eafd', padding: '20px', borderRadius: '8px', marginBottom: '20px', border: '1px solid #d6b8f5' }} className="no-print">
                      <h3 style={{ marginTop: 0 }}>🔄 تحويل الموظف لنوبة أخرى</h3>
                      <p style={{ color: '#666' }}>النوبة الحالية: <strong>{selectedEmployee.shift}</strong> {selectedEmployee.shiftType === 'صباحي' ? '(صباحي)' : '(نوبات)'}</p>
                      <form onSubmit={handleChangeShift}>
                        <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px' }}>النوبة الجديدة:</label>
                        <select value={shiftForm.newShift} onChange={(e) => setShiftForm({ ...shiftForm, newShift: e.target.value })} style={inputStyle}>
                          {SHIFT_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                        </select>
                        <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px' }}>تاريخ التحويل:</label>
                        <input type="date" dir="ltr" lang="en-CA" value={shiftForm.changeDate} onChange={(e) => setShiftForm({ ...shiftForm, changeDate: e.target.value })} style={dateInputStyle} required />
                        <input type="text" placeholder="ملاحظات" value={shiftForm.notes} onChange={(e) => setShiftForm({ ...shiftForm, notes: e.target.value })} style={inputStyle} />
                        <button type="submit" style={{ ...btn, backgroundColor: '#8e44ad', marginLeft: '10px' }}>تأكيد التحويل</button>
                        <button type="button" onClick={() => setShowChangeShift(false)} style={{ ...btn, backgroundColor: '#95a5a6' }}>إلغاء</button>
                      </form>
                    </div>
                  )}

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '15px', marginBottom: '20px' }}>
                    {[
                      ['رقم الملف', selectedEmployee.fileNo],
                      ['الرقم المدني', selectedEmployee.civilID],
                      ['النوبة', `${selectedEmployee.shift} ${selectedEmployee.shiftType === 'صباحي' ? '(صباحي)' : ''}`],
                      ['مكان العمل', selectedEmployee.workLocation || '-'],
                      ['المسمى الوظيفي', selectedEmployee.jobTitle || '-'],
                      ['الهاتف', selectedEmployee.phone || '-'],
                      ['الحالة', selectedArchived ? 'مؤرشف' : 'نشط']
                    ].map(([label, value]) => (
                      <div key={label} style={{ padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '6px' }}>
                        <p style={{ margin: '0 0 5px 0', color: '#999', fontSize: '12px' }}>{label}</p>
                        <p style={{ margin: 0, fontWeight: 'bold', fontSize: '15px' }}>{value}</p>
                      </div>
                    ))}
                  </div>

                  {(() => {
                    const stats = calculateStats();
                    const cards = [
                      ['أيام العمل', stats.workDays, '#3498db', '#e3f2fd'],
                      ['أيام الدوام المتأثرة', stats.workImpactDays, '#34495e', '#ecf0f1'],
                      ['دورية', stats.vacation, '#27ae60', '#e8f5e9'],
                      ['عارضة', stats.emergency, '#f39c12', '#fff3e0'],
                      ['مرضية', stats.sick, '#e74c3c', '#fce4ec'],
                      ['غياب', stats.absence, '#9b59b6', '#f3e5f5'],
                      ['مهمة رسمية', stats.mission, '#16a085', '#e0f2f1'],
                    ];
                    return (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '12px' }}>
                        {cards.map(([label, value, color, bg]) => (
                          <div key={label} style={{ padding: '15px', backgroundColor: bg, borderRadius: '8px' }}>
                            <p style={{ margin: '0 0 8px 0', color: '#666', fontSize: '13px' }}>{label}</p>
                            <p style={{ margin: 0, fontSize: '28px', fontWeight: 'bold', color }}>{value}</p>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>

                {shiftHistory.length > 0 && (
                  <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', marginBottom: '20px' }}>
                    <h3 style={{ marginTop: 0 }}>📜 سجل تغييرات النوبة</h3>
                    <Table headers={['من نوبة', 'إلى نوبة', 'تاريخ التحويل', 'ملاحظات']}>
                      {shiftHistory.map(h => (
                        <tr key={h.id}>
                          <td style={td}>{h.fromShift} {h.fromShiftType === 'صباحي' ? '(صباحي)' : ''}</td>
                          <td style={td}>{h.toShift} {h.toShiftType === 'صباحي' ? '(صباحي)' : ''}</td>
                          <td style={td}>{h.changeDate}</td>
                          <td style={td}>{h.notes || '-'}</td>
                        </tr>
                      ))}
                    </Table>
                  </div>
                )}

                {canEnterEmployeeRequest && !selectedArchived && (
                  <button onClick={() => setShowAddLeave(true)} className="no-print" style={{ ...btn, marginBottom: '20px', backgroundColor: '#27ae60', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Plus size={18} /> إدخال طلب للموظف
                  </button>
                )}

                {showAddLeave && (
                  <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', marginBottom: '20px' }} className="no-print">
                    <h3 style={{ marginTop: 0 }}>إدخال طلب نيابة عن الموظف</h3>
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: 14, color: '#555', marginBottom: 8 }}>الأزرار الرئيسية للمحرر/المراقب:</div>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {[
                          ['vacation', 'إجازة دورية', '#27ae60'],
                          ['emergency', 'إجازة عارضة', '#f39c12'],
                          ['extension', 'تمديد إجازة', '#8e44ad'],
                          ['returnToWork', 'مباشرة عمل', '#16a085']
                        ].map(([nextType, label, color]) => (
                          <button
                            key={nextType}
                            type="button"
                            onClick={() => {
                              const annual = getSelectedAnnualFollowupOption(leaveRecords, leaveForm.relatedLeaveId);
                              setLeaveForm({
                                ...leaveForm,
                                type: nextType,
                                startDate: nextType === 'extension' ? (annual?.suggestedExtensionStartDate || '') : (nextType === 'returnToWork' ? (annual?.suggestedReturnDate || '') : leaveForm.startDate),
                                endDate: nextType === 'emergency' || nextType === 'returnToWork' ? '' : leaveForm.endDate,
                                relatedLeaveId: nextType === 'extension' || nextType === 'returnToWork' ? (annual?.id || '') : ''
                              });
                            }}
                            style={{ ...btn, backgroundColor: leaveForm.type === nextType ? color : '#ecf0f1', color: leaveForm.type === nextType ? 'white' : '#2c3e50', padding: '8px 12px', border: leaveForm.type === nextType ? '2px solid #2c3e50' : '1px solid #bdc3c7' }}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                      <div style={{ fontSize: 12, color: '#777', marginTop: 8 }}>التمديد والمباشرة يتم التحقق منها تلقائيًا وربطها بإجازة دورية معتمدة قبل الحفظ.</div>
                    </div>
                    <form onSubmit={handleAddLeave}>
                      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 280px) repeat(2, minmax(160px, 220px))', gap: '12px', alignItems: 'end', marginBottom: '10px' }}>
                        <div>
                          <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px' }}>نوع الإجازة:</label>
                          <select value={leaveForm.type} onChange={(e) => {
                            const nextType = e.target.value;
                            const annual = getSelectedAnnualFollowupOption(leaveRecords, leaveForm.relatedLeaveId);
                            setLeaveForm({
                              ...leaveForm,
                              type: nextType,
                              startDate: nextType === 'extension' ? (annual?.suggestedExtensionStartDate || '') : (nextType === 'returnToWork' ? (annual?.suggestedReturnDate || '') : leaveForm.startDate),
                              endDate: nextType === 'emergency' || nextType === 'returnToWork' ? '' : leaveForm.endDate,
                              relatedLeaveId: nextType === 'extension' || nextType === 'returnToWork' ? (annual?.id || '') : ''
                            });
                          }} style={{ ...inputStyle, marginBottom: 0 }}>
                            {Object.entries(TYPE_LABELS).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
                          </select>
                        </div>
                        {(leaveForm.type === 'extension' || leaveForm.type === 'returnToWork') && (() => {
                          const annualOptions = getAnnualFollowupOptions(leaveRecords);
                          const annual = getSelectedAnnualFollowupOption(leaveRecords, leaveForm.relatedLeaveId);
                          return (
                            <div style={{ gridColumn: '1 / -1', background: annual ? '#eef9f5' : '#fff5f5', border: `1px solid ${annual ? '#a8e6cf' : '#f1b0b7'}`, padding: 10, borderRadius: 8 }}>
                              {annual ? (
                                <>
                                  <strong>الإجازة الدورية المرتبطة:</strong>
                                  {annualOptions.length > 1 && (
                                    <select
                                      value={annual.id || ''}
                                      onChange={(e) => {
                                        const option = annualOptions.find(item => String(item.id) === String(e.target.value));
                                        if (!option) return;
                                        setLeaveForm({
                                          ...leaveForm,
                                          relatedLeaveId: option.id,
                                          startDate: leaveForm.type === 'extension' ? option.suggestedExtensionStartDate : option.suggestedReturnDate
                                        });
                                      }}
                                      style={{ ...inputStyle, marginTop: 8, marginBottom: 6 }}
                                    >
                                      {annualOptions.map(option => (
                                        <option key={option.id} value={option.id}>إجازة دورية من {option.startDate} إلى {option.endDate}</option>
                                      ))}
                                    </select>
                                  )}
                                  <div dir="ltr" style={{ marginTop: 5 }}>{annual.startDate} → {annual.endDate}</div>
                                  {leaveForm.type === 'extension' && <div style={{ marginTop: 5 }}>بداية التمديد تلقائيًا: <span dir="ltr">{annual.suggestedExtensionStartDate}</span></div>}
                                  {leaveForm.type === 'returnToWork' && <div style={{ marginTop: 5 }}>تاريخ المباشرة المقترح: <span dir="ltr">{annual.suggestedReturnDate}</span></div>}
                                </>
                              ) : <strong>لا توجد إجازة دورية معتمدة متاحة للتمديد أو المباشرة.</strong>}
                            </div>
                          );
                        })()}
                        {!(leaveForm.type === 'extension' && getSelectedAnnualFollowupOption(leaveRecords, leaveForm.relatedLeaveId)) && (
                          <div>
                            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px' }}>{leaveForm.type === 'emergency' ? 'تاريخ العارضة:' : leaveForm.type === 'returnToWork' ? 'تاريخ المباشرة:' : 'تاريخ البداية:'}</label>
                            <input type="date" dir="ltr" lang="en-CA" value={leaveForm.startDate} onChange={(e) => setLeaveForm({ ...leaveForm, startDate: e.target.value })} style={dateInputStyle} required />
                          </div>
                        )}
                        {leaveForm.type !== 'emergency' && leaveForm.type !== 'returnToWork' && (
                          <div>
                            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px' }}>{leaveForm.type === 'extension' ? 'تاريخ نهاية التمديد:' : 'تاريخ النهاية:'}</label>
                            <input type="date" dir="ltr" lang="en-CA" value={leaveForm.endDate} onChange={(e) => setLeaveForm({ ...leaveForm, endDate: e.target.value })} style={dateInputStyle} required />
                          </div>
                        )}
                      </div>
                      <textarea placeholder="ملاحظات" value={leaveForm.notes} onChange={(e) => setLeaveForm({ ...leaveForm, notes: e.target.value })} style={{ ...inputStyle, minHeight: '70px' }} />
                      <button type="submit" style={{ ...btn, backgroundColor: '#27ae60', marginLeft: '10px' }}>حفظ</button>
                      <button type="button" onClick={() => setShowAddLeave(false)} style={{ ...btn, backgroundColor: '#95a5a6' }}>إلغاء</button>
                    </form>
                  </div>
                )}

                <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px' }}>
                  <h3 style={{ marginTop: 0 }}>سجل الإجازات والمواقف</h3>
                  {leaveRecords.length === 0 ? (
                    <p style={{ color: '#999', textAlign: 'center', padding: '20px' }}>لا توجد بيانات مسجلة</p>
                  ) : (
                    <Table headers={['تاريخ البداية', 'تاريخ النهاية', 'تاريخ مباشرة العمل', 'نوع السجل', 'عدد أيام الإجازة', 'حالة الاعتماد', 'مدخل السجل', 'المعتمد بواسطة', 'آخر تعديل', 'ملاحظات', 'إجراءات']}>
                      {leaveRecords.map(r => (
                        <tr key={r.id}>
                          <td style={td}>{r.startDate}</td>
                          <td style={td}>
                            {r.endDate}
                            {r.extended && (
                              <div style={{ fontSize: '11px', color: '#8e44ad', fontWeight: 'bold' }}>
                                مُمدّدة (الأصلي: {r.originalEndDate})
                              </div>
                            )}
                            {r.pendingExtension?.status === 'pending' && (
                              <div style={{ fontSize: '11px', color: '#d35400', fontWeight: 'bold' }}>
                                طلب تمديد بانتظار الاعتماد إلى {r.pendingExtension.requestedNewEndDate}
                              </div>
                            )}
                          </td>
                          <td style={td}>{r.returnToWorkDate || returnDate(r.endDate)}</td>
                          <td style={td}>{TYPE_LABELS[r.type] || r.type}</td>
                          <td style={{ ...td, fontWeight: 'bold' }}>{r.actualDays ?? r.days}</td>
                          <td style={td}>
                            {r.approvalStatus === 'pending' ? 'بانتظار الاعتماد' :
                              r.approvalStatus === 'approved' ? 'تم الاعتماد' :
                              r.approvalStatus === 'rejected' ? 'مرفوضة' :
                              r.extensionApprovalStatus === 'pending' ? 'تمديد بانتظار الاعتماد' :
                              'لا يحتاج اعتماد'}
                          </td>
                          <td style={td}>{r.createdByName || '-'}</td>
                          <td style={td}>
                            {r.approvedByName || r.extensionApprovedByName || '-'}
                            {(r.approvedAt || r.extensionApprovedAt) && (
                              <div style={{ fontSize: 11, color: '#666' }}>
                                {String(r.approvedAt || r.extensionApprovedAt).slice(0, 10)}
                              </div>
                            )}
                          </td>
                          <td style={td}>{r.updatedByName || '-'}</td>
                          <td style={td}>{r.notes || '-'}</td>
                          <td style={td} className="no-print">
                            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                              {r.type === 'vacation' && (
                                <>
                                  <button
                                    onClick={() => printOfficialForm('leave', { employee: selectedEmployee, leave: r })}
                                    style={{ padding: '5px 10px', backgroundColor: '#1aa3a3', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '12px' }}>
                                    طباعة طلب إجازة
                                  </button>
                                  <button
                                    onClick={() => printOfficialForm('return', { employee: selectedEmployee, leave: r })}
                                    style={{ padding: '5px 10px', backgroundColor: '#2c7a7a', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '12px' }}>
                                    طباعة مباشرة عمل
                                  </button>
                                  {r.extended && (
                                    <button
                                      onClick={() => printOfficialForm('extension', { employee: selectedEmployee, leave: r })}
                                      style={{ padding: '5px 10px', backgroundColor: '#16a085', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '12px' }}>
                                      طباعة طلب تمديد
                                    </button>
                                  )}
                                  {canEdit && (
                                    <button onClick={() => handleExtendLeave(r)} style={{ padding: '5px 10px', backgroundColor: '#2980b9', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '12px' }}>تمديد</button>
                                  )}
                                </>
                              )}
                              {r.type === 'emergency' && (
                                <button
                                  onClick={() => printOfficialForm('emergency', { employee: selectedEmployee, leave: r })}
                                  style={{ padding: '5px 10px', backgroundColor: '#1aa3a3', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '12px' }}>
                                  طباعة طلب عارضة
                                </button>
                              )}
                              {r.type === 'extension' && (
                                <button
                                  onClick={() => printOfficialForm('extension', { employee: selectedEmployee, leave: r })}
                                  style={{ padding: '5px 10px', backgroundColor: '#16a085', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '12px' }}>
                                  طباعة طلب تمديد
                                </button>
                              )}
                              {r.type === 'returnToWork' && (
                                <button
                                  onClick={() => printOfficialForm('return', { employee: selectedEmployee, leave: r })}
                                  style={{ padding: '5px 10px', backgroundColor: '#2c7a7a', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '12px' }}>
                                  طباعة مباشرة عمل
                                </button>
                              )}
                              {canEdit && (
                                <button onClick={() => handleDeleteLeave(r.id)} style={{ padding: '5px 10px', backgroundColor: '#e74c3c', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '12px' }}>حذف</button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </Table>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        {activeTab === 'report' && canViewReportsAndStatements && (
          <ReportTab
            reportShift={reportShift}
            setReportShift={setReportShift}
            fetchReport={fetchReport}
            reportLoading={reportLoading}
            reportData={reportData}
          />
        )}

        {activeTab === 'locationReport' && canViewReportsAndStatements && (
          <LocationReportTab
            filters={locationFilters}
            setFilters={setLocationFilters}
            fetchReport={fetchLocationReport}
            loading={locationLoading}
            rows={locationReport}
          />
        )}

        {activeTab === 'leaveStatement' && canViewReportsAndStatements && (
          <LeaveStatementTab
            employees={employees}
            fetchEmployees={fetchEmployees}
            statementSearch={statementSearch}
            setStatementSearch={setStatementSearch}
            statementEmployeeList={statementEmployeeList}
            selectedIds={selectedStatementIds}
            toggleEmployee={toggleStatementEmployee}
            selectAll={selectAllStatementFiltered}
            clearSelection={clearStatementSelection}
            filters={statementFilters}
            setFilters={setStatementFilters}
            fetchStatement={fetchLeaveStatement}
            loading={statementLoading}
            rows={statementRows}
          />
        )}

        {activeTab === 'returnStatement' && canViewReportsAndStatements && (
          <ReturnStatementTab
            search={returnStatementSearch}
            setSearch={setReturnStatementSearch}
            filters={returnStatementFilters}
            setFilters={setReturnStatementFilters}
            fetchStatement={fetchReturnStatement}
            loading={returnStatementLoading}
            rows={returnStatementRows}
          />
        )}

        {activeTab === 'addEmployee' && canEdit && (
          <div style={{ backgroundColor: 'white', padding: '30px', borderRadius: '8px', marginBottom: '20px' }}>
            <h3 style={{ marginTop: 0 }}>إضافة موظف جديد</h3>
            <form onSubmit={handleAddEmployee}>
              <input type="text" placeholder="الاسم الكامل" value={employeeForm.fullName} onChange={(e) => setEmployeeForm({ ...employeeForm, fullName: e.target.value })} style={inputStyle} required />
              <input type="text" placeholder="الرقم المدني" value={employeeForm.civilID} onChange={(e) => setEmployeeForm({ ...employeeForm, civilID: e.target.value })} style={inputStyle} required />
              <input type="text" placeholder="رقم الملف" value={employeeForm.fileNo} onChange={(e) => setEmployeeForm({ ...employeeForm, fileNo: e.target.value })} style={inputStyle} required />
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px' }}>النوبة / نظام الدوام:</label>
              <select value={employeeForm.shift} onChange={(e) => setEmployeeForm({ ...employeeForm, shift: e.target.value })} style={inputStyle}>
                {SHIFT_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              </select>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px' }}>مكان العمل:</label>
              <select value={employeeForm.workLocation} onChange={(e) => setEmployeeForm({ ...employeeForm, workLocation: e.target.value })} style={inputStyle}>
                <option value="">غير محدد</option>
                {WORK_LOCATIONS.map(item => <option key={item} value={item}>{item}</option>)}
              </select>
              <input type="text" placeholder="المسمى الوظيفي" value={employeeForm.jobTitle} onChange={(e) => setEmployeeForm({ ...employeeForm, jobTitle: e.target.value })} style={inputStyle} />
              <input type="text" placeholder="رقم الهاتف" value={employeeForm.phone} onChange={(e) => setEmployeeForm({ ...employeeForm, phone: e.target.value })} style={inputStyle} />
              <button type="submit" style={{ ...btn, backgroundColor: '#27ae60', marginLeft: '10px' }}>حفظ الموظف</button>
              <button type="button" onClick={() => setActiveTab('search')} style={{ ...btn, backgroundColor: '#95a5a6' }}>إلغاء</button>
            </form>
          </div>
        )}

        {activeTab === 'archive' && (
          canViewArchive ? <ArchiveTab canRestore={isAdmin} onRestore={handleRestoreEmployee} onOpen={(emp) => { openEmployee(emp); setActiveTab('search'); }} /> : null
        )}

        {activeTab === 'approvals' && (
          isAdmin ? <LeaveApprovalsTab onChanged={() => {
            fetchEmployees(false);
            if (selectedEmployee) refreshSelectedEmployee(selectedEmployee.id);
          }} /> : null
        )}

        {activeTab === 'officialHolidays' && (
          isAdmin ? <OfficialHolidaysTab version={officialHolidaysVersion} setVersion={setOfficialHolidaysVersion} /> : null
        )}

        {activeTab === 'shiftSettings' && (
          isAdmin ? <ShiftSettingsTab version={shiftSystemsVersion} setVersion={setShiftSystemsVersion} /> : null
        )}

        {activeTab === 'auditLogs' && (
          isAdmin ? <AuditLogsTab /> : null
        )}

        {activeTab === 'systemUpdate' && (
          isAdmin ? <SystemUpdateTab currentUser={currentUser} systemVersionInfo={systemVersionInfo} onBumped={(data) => {
            if (data?.refreshKey) localStorage.setItem(CLIENT_REFRESH_KEY, String(data.refreshKey));
            setSystemVersionInfo(data);
            setUpdateNotice(null);
          }} /> : null
        )}

        {activeTab === 'manage' && (
          (isAdmin || isEditor) ? <UserManagement currentUser={currentUser} /> : (
            <div style={{ backgroundColor: 'white', padding: '30px', borderRadius: '8px' }}>
              <h2 style={{ marginTop: 0, color: '#2c3e50' }}>الإدارة</h2>
              <p style={{ color: '#e74c3c', fontSize: '16px' }}>هذه الصفحة متاحة للمراقب فقط.</p>
            </div>
          )
        )}
      </div>

      <div className="no-print" style={{
        textAlign: 'center',
        color: '#777',
        fontSize: '12px',
        padding: '15px',
        borderTop: '1px solid #ddd',
        backgroundColor: 'white'
      }}>
        <span style={{
          display: 'inline-block',
          width: '20px',
          height: '13px',
          marginInlineEnd: '6px',
          verticalAlign: '-2px',
          border: '1px solid #bbb',
          position: 'relative',
          background: 'linear-gradient(to bottom, #007A3D 0 33.33%, #FFFFFF 33.33% 66.66%, #CE1126 66.66% 100%)'
        }} aria-label="Kuwait flag" title="Kuwait flag">
          <span style={{
            position: 'absolute',
            left: 0,
            top: 0,
            width: '45%',
            height: '100%',
            backgroundColor: '#000000',
            clipPath: 'polygon(0 0, 100% 50%, 0 100%)'
          }}></span>
        </span>
        <span dir="ltr" style={{ fontWeight: 'bold' }}>Developed by Abdullah Faleh Aljber © 2026. All rights reserved.</span>
        <span style={{ margin: '0 8px', color: '#777' }}>|</span>
        <span>نظام متابعة الدوام والإجازات - مراقبة التشغيل / محطة الصبية</span>
      </div>

      <style>{`
        @media print {
          .no-print { display: none !important; }
          html, body, #root { background: white !important; margin: 0 !important; padding: 0 !important; }
          table { page-break-inside: auto; border-collapse: collapse !important; }
          tr { page-break-inside: avoid; page-break-after: auto; }
          th, td { font-size: 11px !important; background: #fff !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .official-header { margin: 0 0 12px 0 !important; page-break-inside: avoid !important; }
          .official-header-row { padding-bottom: 7px !important; }
        }
      `}</style>
    </div>
  );
}


function statusBadge(status) {
  const colors = {
    pending_shift_supervisor: '#f39c12',
    late_shift_supervisor: '#e67e22',
    rejected_by_shift_supervisor: '#e74c3c',
    pending_manager: '#3498db',
    approved: '#27ae60',
    rejected_by_manager: '#c0392b',
    cancelled_by_employee: '#7f8c8d',
    cancelled: '#7f8c8d'
  };
  return colors[status] || '#7f8c8d';
}


function NotificationsPanel({ currentUser }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const lang = currentUser?.language || 'ar';
  const isEnglish = lang === 'en';

  const load = async () => {
    setLoading(true);
    try {
      const res = await apiFetch(`${API_URL}/notifications`);
      const data = await res.json();
      if (res.ok) setItems(data.notifications || []);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { load(); }, []);
  const markRead = async (id) => {
    try {
      await apiFetch(`${API_URL}/notifications/${id}/read`, { method: 'POST', body: JSON.stringify({}) });
      setItems(prev => prev.map(item => item.id === id ? { ...item, read: true } : item));
    } catch {}
  };

  const unread = items.filter(item => item.read !== true).slice(0, 8);
  if (!loading && unread.length === 0) return null;

  return (
    <div className="no-print" style={{ maxWidth: '1400px', margin: '16px auto 0', padding: '0 20px' }}>
      <div style={{ backgroundColor: '#fff9e6', border: '1px solid #f1d27a', borderRadius: 8, padding: '12px 14px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <strong>{isEnglish ? 'Notifications' : 'الإشعارات'}</strong>
          <button onClick={load} style={{ ...btn, backgroundColor: '#3498db', padding: '6px 12px' }}>{isEnglish ? 'Refresh' : 'تحديث'}</button>
        </div>
        {loading ? <div style={{ color: '#666', marginTop: 8 }}>{isEnglish ? 'Loading...' : 'جاري التحميل...'}</div> : (
          <div style={{ display: 'grid', gap: 8, marginTop: 10 }}>
            {unread.map(item => (
              <div key={item.id} style={{ background: 'white', border: '1px solid #eee', borderRadius: 6, padding: '8px 10px', display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center' }}>
                <div>
                  <div style={{ fontWeight: 'bold' }}>{isEnglish ? (item.titleEn || item.titleAr) : (item.titleAr || item.titleEn)}</div>
                  <div style={{ fontSize: 13, color: '#666' }}>{isEnglish ? (item.bodyEn || item.bodyAr) : (item.bodyAr || item.bodyEn)}</div>
                </div>
                <button onClick={() => markRead(item.id)} style={{ ...btn, backgroundColor: '#7f8c8d', padding: '5px 10px', whiteSpace: 'nowrap' }}>{isEnglish ? 'Mark read' : 'تم الاطلاع'}</button>
              </div>
            ))}
            {unread.length === 0 && <div style={{ color: '#666' }}>{isEnglish ? 'No new notifications.' : 'لا توجد إشعارات جديدة.'}</div>}
          </div>
        )}
      </div>
    </div>
  );
}

function ProfileTab({ currentUser, onUserUpdate }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');
  const lang = profile?.language || currentUser?.language || 'ar';
  const en = lang === 'en';

  const load = async () => {
    setLoading(true);
    setMsg('');
    try {
      const res = await apiFetch(`${API_URL}/my-profile`);
      const data = await res.json();
      if (res.ok) setProfile(data.profile || {});
      else setMsg(data.error || 'تعذر تحميل البيانات');
    } catch { setMsg('تعذر الاتصال بالخادم'); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);
  const save = async (e) => {
    e.preventDefault();
    setMsg('');
    try {
      const res = await apiFetch(`${API_URL}/my-profile`, { method: 'PUT', body: JSON.stringify({ phone: profile.phone, email: profile.email, language: profile.language }) });
      const data = await res.json();
      if (res.ok) {
        setMsg(profile.language === 'en' ? 'Saved successfully ✅' : 'تم الحفظ بنجاح ✅');
        if (data.user && onUserUpdate) onUserUpdate(data.user);
      } else setMsg(data.error || 'تعذر الحفظ');
    } catch { setMsg('تعذر الاتصال بالخادم'); }
  };

  if (loading || !profile) {
    return <div style={{ backgroundColor: 'white', padding: 25, borderRadius: 8 }}>{loading ? 'جاري التحميل...' : msg}</div>;
  }

  const labels = {
    title: en ? 'My Profile' : 'بياناتي',
    readOnly: en ? 'Official Information (view only)' : 'البيانات الرسمية (عرض فقط)',
    name: en ? 'Name' : 'الاسم',
    civilID: en ? 'Civil ID' : 'الرقم المدني',
    fileNo: en ? 'File No.' : 'رقم الملف',
    shift: en ? 'Shift' : 'الشفت / النوبة',
    workLocation: en ? 'Work Location' : 'مكان العمل',
    jobTitle: en ? 'Job Title' : 'المسمى الوظيفي',
    contact: en ? 'Contact Information' : 'بيانات التواصل',
    phone: en ? 'Phone Number' : 'رقم الهاتف',
    email: en ? 'Email' : 'الإيميل',
    language: en ? 'Language' : 'اللغة',
    save: en ? 'Save' : 'حفظ'
  };

  return (
    <div style={{ backgroundColor: 'white', padding: 25, borderRadius: 8 }}>
      <h2 style={{ marginTop: 0 }}>{labels.title}</h2>
      <div style={{ backgroundColor: '#f8f9fa', padding: 20, borderRadius: 8, border: '1px solid #ddd', marginBottom: 20 }}>
        <h3 style={{ marginTop: 0 }}>{labels.readOnly}</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
          <div><strong>{labels.name}</strong><br />{profile.fullName || '-'}</div>
          <div><strong>{labels.civilID}</strong><br />{profile.civilID || '-'}</div>
          <div><strong>{labels.fileNo}</strong><br />{profile.fileNo || '-'}</div>
          <div><strong>{labels.shift}</strong><br />{displayShiftValue(profile.shift, lang)}</div>
          <div><strong>{labels.workLocation}</strong><br />{displayWorkLocation(profile.workLocation, lang)}</div>
          <div><strong>{labels.jobTitle}</strong><br />{displayJobTitle(profile.jobTitle, lang)}</div>
        </div>
      </div>

      <form onSubmit={save} style={{ backgroundColor: '#fff', padding: 20, borderRadius: 8, border: '1px solid #ddd' }}>
        <h3 style={{ marginTop: 0 }}>{labels.contact}</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
          <div>
            <label>{labels.phone}</label>
            <input value={profile.phone || ''} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} style={inputStyle} />
          </div>
          <div>
            <label>{labels.email}</label>
            <input type="email" value={profile.email || ''} onChange={(e) => setProfile({ ...profile, email: e.target.value })} style={inputStyle} />
          </div>
          <div>
            <label>{labels.language}</label>
            <select value={profile.language || 'ar'} onChange={(e) => setProfile({ ...profile, language: e.target.value })} style={inputStyle}>
              <option value="ar">عربي</option>
              <option value="en">English</option>
            </select>
          </div>
        </div>
        <button type="submit" style={{ ...btn, backgroundColor: '#27ae60' }}>{labels.save}</button>
        {msg && <span style={{ marginRight: 12, color: msg.includes('✅') ? '#27ae60' : '#e74c3c', fontWeight: 'bold' }}>{msg}</span>}
      </form>
    </div>
  );
}


function requestTypeLabel(type, lang = 'ar') {
  const en = { vacation: 'Annual Leave', emergency: 'Emergency Leave', extension: 'Extension Request', returnToWork: 'Return to Work' };
  const ar = { vacation: 'إجازة دورية', emergency: 'إجازة عارضة', extension: 'تمديد إجازة', returnToWork: 'مباشرة عمل' };
  return (lang === 'en' ? en : ar)[type] || type;
}

function requestWorkflowLabel(status, lang = 'ar') {
  const en = {
    pending_shift_supervisor: 'Pending Shift Supervisor Approval',
    late_shift_supervisor: 'Delayed with Shift Supervisor',
    rejected_by_shift_supervisor: 'Rejected by Shift Supervisor',
    pending_manager: 'Pending Manager Final Approval',
    pending_editor_review: 'Pending Editor Review / Send to Manager',
    approved: 'Final Approved',
    rejected_by_manager: 'Rejected by Manager',
    cancelled_by_employee: 'Cancelled by Employee',
    cancelled: 'Cancelled'
  };
  const ar = {
    pending_shift_supervisor: 'بانتظار اعتماد مسؤول النوبة',
    late_shift_supervisor: 'متأخر لدى مسؤول النوبة',
    rejected_by_shift_supervisor: 'مرفوض من مسؤول النوبة',
    pending_manager: 'بانتظار الاعتماد النهائي من المراقب',
    pending_editor_review: 'بانتظار مراجعة المحرر / الإرسال للمراقب',
    approved: 'تم الاعتماد النهائي',
    rejected_by_manager: 'مرفوض من المراقب',
    cancelled_by_employee: 'ملغى من الموظف',
    cancelled: 'ملغى'
  };
  return (lang === 'en' ? en : ar)[status] || status || '-';
}

function englishDate(value) {
  if (!value) return '-';
  return String(value).slice(0, 10);
}


function displayShiftValue(value, lang = 'ar') {
  const v = String(value || '').trim();
  if (lang !== 'en') return v || '-';
  if (v === 'M') return 'Morning (M)';
  if (['A', 'B', 'C', 'D', 'F'].includes(v)) return `Shift ${v}`;
  return v || '-';
}

function displayWorkLocation(value, lang = 'ar') {
  const v = String(value || '').trim();
  if (lang !== 'en') return v || '-';
  const map = { 'صباحي': 'Morning', 'مراقبة التشغيل': 'Operations Monitoring' };
  return map[v] || v || '-';
}

function displayJobTitle(value, lang = 'ar') {
  const v = String(value || '').trim();
  if (lang !== 'en') return v || '-';
  const map = { 'مهندس': 'Engineer', 'رئيس قسم': 'Section Head', 'مشرف': 'Supervisor', 'فني': 'Technician', 'مشغل': 'Operator', 'مراقب': 'Controller' };
  return map[v] || v || '-';
}

function RequestTypeSelect({ value, onChange, language = 'ar' }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} style={inputStyle}>
      <option value="vacation">{requestTypeLabel('vacation', language)}</option>
      <option value="emergency">{requestTypeLabel('emergency', language)}</option>
      <option value="extension">{requestTypeLabel('extension', language)}</option>
      <option value="returnToWork">{requestTypeLabel('returnToWork', language)}</option>
    </select>
  );
}

function isRowApproved(row) {
  return (row?.workflowStatus || row?.approvalStatus || '') === 'approved';
}

function isRowActive(row) {
  const status = row?.workflowStatus || row?.approvalStatus || '';
  return !['cancelled', 'cancelled_by_employee', 'rejected_by_manager', 'rejected_by_shift_supervisor', 'cancelled_by_admin', 'rejected'].includes(status);
}

function rowLinkedTo(row, id) {
  if (!row || !id) return false;
  return [row.relatedLeaveId, row.sourceLeaveId, row.originalLeaveId, row.parentLeaveId]
    .map(x => String(x || '').trim())
    .filter(Boolean)
    .includes(String(id));
}

function getAnnualFollowupOptions(records = []) {
  const activeReturns = records.filter(r => normalizeLeaveType(r.type) === 'returnToWork' && isRowActive(r));
  const activePendingFollowups = records.filter(r => ['extension', 'returnToWork'].includes(normalizeLeaveType(r.type)) && isRowActive(r) && !isRowApproved(r));
  const approvedExtensions = records.filter(r => normalizeLeaveType(r.type) === 'extension' && isRowApproved(r));
  const annuals = records
    .filter(r => normalizeLeaveType(r.type) === 'vacation' && isRowApproved(r))
    .map(annual => {
      const linkedExt = approvedExtensions.filter(ext => rowLinkedTo(ext, annual.id));
      const latestEnd = linkedExt.reduce((max, ext) => {
        const end = ext.endDate || ext.startDate || '';
        return end && (!max || end > max) ? end : max;
      }, annual.endDate || annual.startDate || '');
      return {
        id: annual.id,
        startDate: annual.startDate || annual.date || '',
        endDate: latestEnd || annual.endDate || annual.startDate || '',
        originalEndDate: annual.endDate || annual.startDate || '',
        extensionCount: linkedExt.length,
        suggestedExtensionStartDate: returnDate(latestEnd || annual.endDate || annual.startDate || ''),
        suggestedReturnDate: returnDate(latestEnd || annual.endDate || annual.startDate || '')
      };
    })
    .filter(annual => {
      const hasReturn = activeReturns.some(r => rowLinkedTo(r, annual.id) || (r.startDate === annual.startDate && r.endDate === annual.endDate));
      const hasPending = activePendingFollowups.some(r => rowLinkedTo(r, annual.id));
      return !hasReturn && !hasPending;
    });
  annuals.sort((a, b) => String(b.endDate || '').localeCompare(String(a.endDate || '')));
  return annuals;
}

function getAnnualFollowupOption(records = []) {
  return getAnnualFollowupOptions(records)[0] || null;
}

function getSelectedAnnualFollowupOption(records = [], relatedLeaveId = '') {
  const options = getAnnualFollowupOptions(records);
  return options.find(option => String(option.id) === String(relatedLeaveId || '')) || options[0] || null;
}

function splitISODate(value) {
  const m = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return { year: '', month: '', day: '' };
  return { year: m[1], month: m[2], day: m[3] };
}

function daysInMonth(year, month) {
  const y = Number(year);
  const m = Number(month);
  if (!y || !m) return 31;
  return new Date(y, m, 0).getDate();
}

function composeISODate(year, month, day) {
  if (!year || !month || !day) return '';
  const y = String(year).padStart(4, '0');
  const m = String(month).padStart(2, '0');
  const d = String(day).padStart(2, '0');
  const check = new Date(`${y}-${m}-${d}T12:00:00`);
  if (Number.isNaN(check.getTime())) return '';
  if (check.getFullYear() !== Number(y) || check.getMonth() + 1 !== Number(m) || check.getDate() !== Number(d)) return '';
  return `${y}-${m}-${d}`;
}

function DatePartsInput({ value, onChange, language = 'ar', required = false }) {
  const isEnglish = language === 'en';
  const [parts, setParts] = useState(splitISODate(value));
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 21 }, (_, i) => currentYear - 10 + i);
  const months = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0'));
  const dayLimit = daysInMonth(parts.year || currentYear, parts.month || '01');
  const days = Array.from({ length: dayLimit }, (_, i) => String(i + 1).padStart(2, '0'));

  useEffect(() => {
    setParts(splitISODate(value));
  }, [value]);

  const update = (key, rawValue) => {
    const next = { ...parts, [key]: rawValue };
    const limit = daysInMonth(next.year || currentYear, next.month || '01');
    if (next.day && Number(next.day) > limit) next.day = String(limit).padStart(2, '0');
    setParts(next);
    onChange(composeISODate(next.year, next.month, next.day));
  };

  const selectStyle = {
    ...inputStyle,
    width: 'auto',
    flex: '1 1 88px',
    minWidth: 82,
    maxWidth: 130,
    marginBottom: 0,
    textAlign: 'center',
    direction: 'ltr',
    padding: '9px 6px'
  };

  return (
    <div>
      <div dir="ltr" style={{ display: 'flex', gap: 8, flexWrap: 'nowrap', justifyContent: 'center', alignItems: 'center' }}>
        <select aria-label={isEnglish ? 'Day' : 'اليوم'} value={parts.day} onChange={(e) => update('day', e.target.value)} style={selectStyle} required={required}>
          <option value="">{isEnglish ? 'Day' : 'يوم'}</option>
          {days.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
        <select aria-label={isEnglish ? 'Month' : 'الشهر'} value={parts.month} onChange={(e) => update('month', e.target.value)} style={selectStyle} required={required}>
          <option value="">{isEnglish ? 'Month' : 'شهر'}</option>
          {months.map(m => <option key={m} value={m}>{m}</option>)}
        </select>
        <select aria-label={isEnglish ? 'Year' : 'السنة'} value={parts.year} onChange={(e) => update('year', e.target.value)} style={selectStyle} required={required}>
          <option value="">{isEnglish ? 'Year' : 'سنة'}</option>
          {years.map(y => <option key={y} value={String(y)}>{y}</option>)}
        </select>
      </div>
      {value && <div style={{ marginTop: 6, color: '#666', fontSize: 13, textAlign: 'center', direction: 'ltr' }}>{value}</div>}
    </div>
  );
}

function MyRequestsTab({ currentUser }) {
  const lang = currentUser?.language || 'ar';
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ type: 'vacation', startDate: '', endDate: '', notes: '' });
  const [message, setMessage] = useState('');
  const [returnPrompt, setReturnPrompt] = useState(null);
  const [returnOptions, setReturnOptions] = useState([]);
  const isEnglish = lang === 'en';
  const selectedAnnual = returnOptions.find(option => String(option.leaveId) === String(form.relatedLeaveId || '')) || returnPrompt || null;

  const load = async () => {
    setLoading(true);
    try {
      const res = await apiFetch(`${API_URL}/my-requests`);
      const data = await res.json();
      if (res.ok) {
        const options = Array.isArray(data.returnOptions) ? data.returnOptions : [];
        setRows(data.requests || []);
        setReturnPrompt(data.returnPrompt || options[0] || null);
        setReturnOptions(options);
      }
      else setMessage(data.error || (isEnglish ? 'Unable to load requests.' : 'تعذر تحميل الطلبات'));
    } catch { setMessage(isEnglish ? 'Cannot connect to server.' : 'تعذر الاتصال بالخادم'); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const validDate = (value) => /^\d{4}-\d{2}-\d{2}$/.test(String(value || ''));
  const needsEndDate = form.type !== 'emergency' && form.type !== 'returnToWork';

  const submit = async (e) => {
    e.preventDefault();
    setMessage('');
    if (!validDate(form.startDate)) {
      setMessage(isEnglish ? 'Date must be in YYYY-MM-DD format.' : 'التاريخ يجب أن يكون بصيغة YYYY-MM-DD');
      return;
    }
    if (needsEndDate && form.endDate && !validDate(form.endDate)) {
      setMessage(isEnglish ? 'End Date must be in YYYY-MM-DD format.' : 'تاريخ النهاية يجب أن يكون بصيغة YYYY-MM-DD');
      return;
    }
    if (needsEndDate && form.endDate && form.endDate < form.startDate) {
      setMessage(isEnglish ? 'Leave end date cannot be before the start date.' : 'تاريخ نهاية الإجازة لا يجوز أن يكون قبل تاريخ البداية.');
      return;
    }
    if ((form.type === 'returnToWork' || form.type === 'extension') && !selectedAnnual?.leaveId && !form.relatedLeaveId) {
      setMessage(isEnglish ? 'No approved annual leave is available for this request.' : 'لا توجد إجازة دورية معتمدة متاحة لهذا الطلب.');
      return;
    }
    const body = { ...form };
    if (form.type === 'emergency' || form.type === 'returnToWork') body.endDate = form.startDate;
    if ((form.type === 'returnToWork' || form.type === 'extension')) body.relatedLeaveId = form.relatedLeaveId || selectedAnnual?.leaveId || '';
    try {
      const res = await apiFetch(`${API_URL}/employee-requests`, { method: 'POST', body: JSON.stringify(body) });
      const data = await res.json();
      if (res.ok) {
        setMessage(data.message || (isEnglish ? 'Request submitted successfully.' : 'تم تقديم الطلب بنجاح.'));
        setForm({ type: 'vacation', startDate: '', endDate: '', notes: '', relatedLeaveId: '' });
        load();
      } else setMessage(data.error || (isEnglish ? 'Unable to submit request.' : 'تعذر إرسال الطلب'));
    } catch { setMessage(isEnglish ? 'Cannot connect to server.' : 'تعذر الاتصال بالخادم'); }
  };

  const canCancel = (row) => {
    const status = row.workflowStatus || row.approvalStatus || '';
    return row.submittedByEmployee === true && ['pending_shift_supervisor', 'late_shift_supervisor', 'pending_editor_review', 'pending_manager', 'pending'].includes(status);
  };

  const cancelRequest = async (row) => {
    const confirmText = isEnglish ? 'Do you want to cancel this request? It will remain in the records as cancelled.' : 'هل تريد إلغاء هذا الطلب؟ سيبقى محفوظًا في السجل كطلب ملغى.';
    if (!window.confirm(confirmText)) return;
    const notes = window.prompt(isEnglish ? 'Cancellation notes (optional):' : 'سبب الإلغاء (اختياري):', '') || '';
    try {
      const res = await apiFetch(`${API_URL}/employee-requests/${row.id}/cancel`, { method: 'POST', body: JSON.stringify({ notes }) });
      const data = await res.json();
      if (res.ok) { setMessage(data.message || (isEnglish ? 'Request cancelled successfully.' : 'تم إلغاء الطلب بنجاح.')); load(); }
      else setMessage(data.error || (isEnglish ? 'Unable to cancel request.' : 'تعذر إلغاء الطلب'));
    } catch { setMessage(isEnglish ? 'Cannot connect to server.' : 'تعذر الاتصال بالخادم'); }
  };

  const msgIsError = /تعذر|Unable|Cannot|must|يجب|format|لا يجوز|cannot/i.test(message || '');

  return (
    <div>
      <div style={{ backgroundColor: 'white', padding: 25, borderRadius: 8, marginBottom: 20 }}>
        <h2 style={{ marginTop: 0 }}>{isEnglish ? 'My Requests' : 'طلباتي'}</h2>
        <p style={{ color: '#666' }}>{isEnglish ? 'Submit and track your leave, emergency, extension, or return-to-work requests.' : 'قدّم طلب إجازة أو عارضة أو تمديد أو مباشرة عمل وتابع حالة الاعتماد.'}</p>
        {returnPrompt && (
          <div style={{ border: '1px solid #f1c40f', background: '#fff8e1', padding: 12, borderRadius: 8, marginBottom: 14 }}>
            <strong>{isEnglish ? 'Annual leave requires action' : 'لديك إجازة دورية تحتاج إجراء'}</strong>
            <div style={{ marginTop: 6 }}>{isEnglish ? 'You can submit a return-to-work request or request an extension for this annual leave.' : 'يمكنك تقديم مباشرة عمل أو طلب تمديد لهذه الإجازة الدورية فقط.'}</div>
            <div style={{ marginTop: 6, direction: 'ltr' }}>{returnPrompt.startDate} → {returnPrompt.endDate}</div>
            {returnPrompt.extensionCount > 0 && <div style={{ marginTop: 4, color: '#7f8c8d' }}>{isEnglish ? `Includes ${returnPrompt.extensionCount} approved extension(s).` : `يشمل ${returnPrompt.extensionCount} تمديد معتمد.`}</div>}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
              <button type="button" onClick={() => setForm({ type: 'returnToWork', startDate: returnPrompt.suggestedReturnDate || '', endDate: '', notes: '', relatedLeaveId: returnPrompt.leaveId })} style={{ ...btn, backgroundColor: '#16a085', padding: '7px 12px' }}>{isEnglish ? 'Prepare return-to-work' : 'تجهيز مباشرة عمل'}</button>
              <button type="button" onClick={() => setForm({ type: 'extension', startDate: returnPrompt.suggestedExtensionStartDate || '', endDate: '', notes: '', relatedLeaveId: returnPrompt.leaveId })} style={{ ...btn, backgroundColor: '#8e44ad', padding: '7px 12px' }}>{isEnglish ? 'Request extension' : 'طلب تمديد'}</button>
            </div>
          </div>
        )}
        <form onSubmit={submit}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 240px))', gap: 12, alignItems: 'end' }}>
            <div>
              <label>{isEnglish ? 'Request Type' : 'نوع الطلب'}</label>
              <RequestTypeSelect value={form.type} language={lang} onChange={(value) => setForm({
                type: value,
                startDate: value === 'returnToWork' ? (selectedAnnual?.suggestedReturnDate || '') : (value === 'extension' ? (selectedAnnual?.suggestedExtensionStartDate || '') : form.startDate),
                endDate: value === 'emergency' || value === 'returnToWork' ? '' : form.endDate,
                notes: form.notes,
                relatedLeaveId: value === 'extension' || value === 'returnToWork' ? (selectedAnnual?.leaveId || '') : ''
              })} />
            </div>
            {(form.type === 'extension' || form.type === 'returnToWork') && (
              <div style={{ gridColumn: '1 / -1', background: selectedAnnual ? '#eef9f5' : '#fff5f5', border: `1px solid ${selectedAnnual ? '#a8e6cf' : '#f1b0b7'}`, padding: 10, borderRadius: 8 }}>
                {selectedAnnual ? (
                  <>
                    <strong>{isEnglish ? 'Linked annual leave' : 'الإجازة الدورية المرتبطة'}</strong>
                    {returnOptions.length > 1 && (
                      <select
                        value={selectedAnnual.leaveId || ''}
                        onChange={(e) => {
                          const option = returnOptions.find(item => String(item.leaveId) === String(e.target.value));
                          if (!option) return;
                          setForm({
                            ...form,
                            relatedLeaveId: option.leaveId,
                            startDate: form.type === 'extension' ? option.suggestedExtensionStartDate : option.suggestedReturnDate
                          });
                        }}
                        style={{ ...inputStyle, marginTop: 8, marginBottom: 6 }}
                      >
                        {returnOptions.map(option => (
                          <option key={option.leaveId} value={option.leaveId}>
                            {isEnglish ? `Annual leave ${option.startDate} to ${option.endDate}` : `إجازة دورية من ${option.startDate} إلى ${option.endDate}`}
                          </option>
                        ))}
                      </select>
                    )}
                    <div style={{ direction: 'ltr', marginTop: 5 }}>{selectedAnnual.startDate} → {selectedAnnual.endDate}</div>
                    {form.type === 'extension' && <div style={{ marginTop: 5 }}>{isEnglish ? 'Extension starts automatically on: ' : 'بداية التمديد تلقائيًا: '}<span dir="ltr">{selectedAnnual.suggestedExtensionStartDate}</span></div>}
                    {form.type === 'returnToWork' && <div style={{ marginTop: 5 }}>{isEnglish ? 'Suggested return date: ' : 'تاريخ المباشرة المقترح: '}<span dir="ltr">{selectedAnnual.suggestedReturnDate}</span></div>}
                  </>
                ) : (
                  <strong>{isEnglish ? 'No approved annual leave is available for extension or return-to-work.' : 'لا توجد إجازة دورية معتمدة متاحة للتمديد أو المباشرة.'}</strong>
                )}
              </div>
            )}
            {!(form.type === 'extension' && selectedAnnual) && (
              <div>
                <label>{form.type === 'returnToWork' ? (isEnglish ? 'Return Date' : 'تاريخ المباشرة') : (isEnglish ? 'Start Date' : 'تاريخ البداية')}</label>
                <DatePartsInput value={form.startDate} language={lang} required onChange={(value) => setForm({ ...form, startDate: value })} />
              </div>
            )}
            {needsEndDate && (
              <div>
                <label>{isEnglish ? 'End Date' : 'تاريخ النهاية'}</label>
                <DatePartsInput value={form.endDate} language={lang} onChange={(value) => setForm({ ...form, endDate: value })} />
              </div>
            )}
          </div>
          <textarea placeholder={isEnglish ? 'Notes' : 'ملاحظات'} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} style={{ ...inputStyle, minHeight: 80 }} />
          <button type="submit" style={{ ...btn, backgroundColor: '#27ae60' }}>{isEnglish ? 'Submit Request' : 'إرسال الطلب'}</button>
          <button type="button" onClick={load} style={{ ...btn, backgroundColor: '#3498db', marginRight: isEnglish ? 0 : 10, marginLeft: isEnglish ? 10 : 0 }}>{isEnglish ? 'Refresh' : 'تحديث'}</button>
          {message && <p style={{ color: msgIsError ? '#e74c3c' : '#27ae60', fontWeight: 'bold' }}>{message}</p>}
        </form>
      </div>

      <div style={{ backgroundColor: 'white', padding: 25, borderRadius: 8 }}>
        <h3 style={{ marginTop: 0 }}>{isEnglish ? 'Request History' : 'سجل طلباتي'}</h3>
        {loading ? <p>{isEnglish ? 'Loading...' : 'جاري التحميل...'}</p> : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', minWidth: 1100, borderCollapse: 'collapse', tableLayout: 'fixed' }}>
              <thead><tr>{(isEnglish ? ['Type', 'Submitted', 'From', 'To', 'Days', 'Status', 'Supervisor', 'Manager', 'Notes', 'Action'] : ['النوع', 'تاريخ التقديم', 'من', 'إلى', 'الأيام', 'الحالة', 'مسؤول النوبة', 'المراقب', 'ملاحظات', 'إجراء']).map(h => <th key={h} style={{ ...th, padding: 8, textAlign: isEnglish ? 'left' : 'right' }}>{h}</th>)}</tr></thead>
              <tbody>
                {rows.length === 0 ? <tr><td colSpan="10" style={{ ...td, textAlign: 'center', color: '#777' }}>{isEnglish ? 'No requests yet.' : 'لا توجد طلبات حتى الآن.'}</td></tr> : rows.map(r => (
                  <tr key={r.id}>
                    <td style={{ ...td, padding: 8 }}>{requestTypeLabel(r.type, lang)}</td>
                    <td style={{ ...td, padding: 8, direction: 'ltr' }}>{englishDate(r.submittedAt || r.createdAt)}</td>
                    <td style={{ ...td, padding: 8, direction: 'ltr' }}>{englishDate(r.startDate)}</td>
                    <td style={{ ...td, padding: 8, direction: 'ltr' }}>{englishDate(r.endDate)}</td>
                    <td style={{ ...td, padding: 8, textAlign: 'center' }}>{r.days || '-'}</td>
                    <td style={{ ...td, padding: 8 }}><span style={{ display: 'inline-block', color: 'white', background: statusBadge(r.workflowStatus), padding: '4px 8px', borderRadius: 12, fontSize: 12 }}>{requestWorkflowLabel(r.workflowStatus, lang)}</span></td>
                    <td style={{ ...td, padding: 8 }}>{r.shiftSupervisorName || '-'}</td>
                    <td style={{ ...td, padding: 8 }}>{r.managerName || r.approvedByName || '-'}</td>
                    <td style={{ ...td, padding: 8 }}>{r.rejectionNotes || r.shiftSupervisorNotes || r.managerNotes || r.cancellationNotes || r.notes || '-'}</td>
                    <td style={{ ...td, padding: 8 }}>{canCancel(r) ? <button onClick={() => cancelRequest(r)} style={{ ...btn, backgroundColor: '#e67e22', padding: '6px 10px', fontSize: 12 }}>{isEnglish ? 'Cancel' : 'إلغاء'}</button> : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function EmployeeRequestsTrackingTab({ currentUser }) {
  const currentPermissions = Array.isArray(currentUser?.permissions) ? currentUser.permissions : [currentUser?.role || ''];
  const canEditorSend = currentPermissions.includes('admin') || currentUser?.role === 'admin' || currentPermissions.includes('editor') || currentUser?.role === 'editor';
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({ q: '', type: '', status: '', shift: '', workLocation: '', from: '', to: '' });
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState('');
  const [lastEditorSend, setLastEditorSend] = useState(null);
  const [selectedEditorIds, setSelectedEditorIds] = useState([]);
  const [bulkEditorProcessing, setBulkEditorProcessing] = useState(false);
  const [bulkEditorResult, setBulkEditorResult] = useState(null);

  const load = async () => {
    setLoading(true);
    const params = new URLSearchParams(Object.entries(filters).filter(([, v]) => v));
    try {
      const res = await apiFetch(`${API_URL}/employee-requests-tracking?${params.toString()}`);
      const data = await res.json();
      if (res.ok) {
        const nextRows = data.requests || [];
        setRows(nextRows);
        setSelectedEditorIds(previous => previous.filter(id => nextRows.some(row => row.id === id && row.workflowStatus === 'pending_editor_review')));
      }
      else alert(data.error || 'تعذر تحميل متابعة الطلبات');
    } catch { alert('تعذر الاتصال بالخادم'); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);
  const set = (key, value) => setFilters(prev => ({ ...prev, [key]: value }));
  const editorEligibleRows = rows.filter(row => row.workflowStatus === 'pending_editor_review');
  const editorEligibleIds = editorEligibleRows.map(row => row.id);
  const selectedVisibleEditorIds = editorEligibleIds.filter(id => selectedEditorIds.includes(id));
  const allEditorEligibleSelected = editorEligibleIds.length > 0 && selectedVisibleEditorIds.length === editorEligibleIds.length;

  const toggleAllEditorEligible = (checked) => {
    setSelectedEditorIds(checked ? editorEligibleIds : []);
  };

  const toggleEditorSelected = (id, checked) => {
    setSelectedEditorIds(previous => checked ? [...new Set([...previous, id])] : previous.filter(value => value !== id));
  };

  const sendSelectedToManager = async () => {
    if (selectedVisibleEditorIds.length === 0) {
      alert('حدد طلبًا واحدًا على الأقل وصل إلى مرحلة المحرر.\nSelect at least one request at editor review.');
      return;
    }
    const notes = window.prompt('ملاحظات محرر مشتركة للطلبات المحددة (اختياري):\nShared editor notes for the selected requests (optional):', '');
    if (notes === null) return;
    const count = selectedVisibleEditorIds.length;
    const confirmed = window.confirm(`سيتم إرسال ${count} طلبًا محددًا للمراقب. هل تريد المتابعة؟\n${count} selected request(s) will be sent to the Manager. Continue?`);
    if (!confirmed) return;
    setBulkEditorProcessing(true);
    try {
      const res = await apiFetch(`${API_URL}/employee-requests/bulk/editor-send-to-manager`, {
        method: 'POST',
        body: JSON.stringify({ requestIds: selectedVisibleEditorIds, notes })
      });
      const data = await res.json();
      if (!res.ok) {
        alert(data.error || 'تعذر إرسال الطلبات المحددة');
        return;
      }
      setBulkEditorResult(data);
      setSelectedEditorIds((data.failures || []).map(item => item.id));
      alert(`نجح: ${data.successCount || 0} — فشل: ${data.failureCount || 0}\nSucceeded: ${data.successCount || 0} — Failed: ${data.failureCount || 0}`);
      await load();
    } catch {
      alert('تعذر الاتصال بالخادم');
    } finally {
      setBulkEditorProcessing(false);
    }
  };

  const undoEditorSend = async (row) => {
    if (!row?.id) return;
    try {
      const res = await apiFetch(`${API_URL}/employee-requests/${row.id}/undo-last-decision`, { method: 'POST' });
      const data = await res.json();
      alert(res.ok ? (data.message || 'تم إرجاع الطلب لمرحلة المحرر') : (data.error || 'تعذر التراجع'));
      if (res.ok) {
        setLastEditorSend(null);
        load();
      }
    } catch { alert('تعذر الاتصال بالخادم'); }
  };

  const sendToManager = async (row) => {
    const bypassingShiftSupervisor = ['pending_shift_supervisor', 'late_shift_supervisor'].includes(row?.workflowStatus);
    const promptText = bypassingShiftSupervisor
      ? 'سبب تجاوز مسؤول النوبة وإرسال الطلب للمراقب (إجباري):\nReason for bypassing Shift Supervisor and sending to Manager (required):'
      : 'ملاحظات المحرر قبل الإرسال للمراقب (اختياري):\nEditor notes before sending to Manager (optional):';
    const notes = window.prompt(promptText, '') || '';
    if (bypassingShiftSupervisor && !notes.trim()) {
      alert('سبب تجاوز مسؤول النوبة مطلوب قبل إرسال الطلب للمراقب.\nA reason is required before bypassing Shift Supervisor approval.');
      return;
    }
    const ok = window.confirm(bypassingShiftSupervisor
      ? 'سيتم تجاوز اعتماد مسؤول النوبة وإرسال الطلب مباشرة للمراقب، وسيتم تسجيل اسم المحرر وسبب التجاوز في السجل. هل تريد المتابعة؟\nThis will bypass Shift Supervisor approval and send the request to the Manager. Your name and reason will be recorded. Continue?'
      : 'سيتم إرسال الطلب للمراقب للاعتماد النهائي. اضغط موافق للإرسال، أو إلغاء لإبقاء الطلب لدى المحرر.');
    if (!ok) return;
    try {
      const res = await apiFetch(`${API_URL}/employee-requests/${row.id}/editor-send-to-manager`, { method: 'POST', body: JSON.stringify({ notes }) });
      const data = await res.json();
      if (res.ok) {
        setLastEditorSend({ id: row.id, employeeName: row.employeeName || '', message: data.message || 'تم إرسال الطلب للمراقب' });
        load();
      } else {
        alert(data.error || 'تعذر إرسال الطلب');
      }
    } catch { alert('تعذر الاتصال بالخادم'); }
  };

  const statusForTracking = (r) => requestWorkflowLabel(r.workflowStatus, 'ar');

  return (
    <div style={{ backgroundColor: 'white', padding: 25, borderRadius: 8 }}>
      <h2 style={{ marginTop: 0 }}>متابعة طلبات الموظفين</h2>
      <p style={{ color: '#666' }}>هذه الصفحة للمحرر/المراقب للاطلاع فقط على طلبات الموظفين من أول التقديم وحتى الاعتماد النهائي.</p>
      {lastEditorSend && (
        <div style={{ background: '#eafaf1', border: '1px solid #27ae60', color: '#145a32', padding: 12, borderRadius: 8, marginBottom: 15, lineHeight: 1.7 }}>
          <div style={{ fontWeight: 'bold' }}>{lastEditorSend.message}</div>
          <div>خرج الطلب من مرحلة المحرر وأصبح بانتظار المراقب. إذا تم الإرسال بالخطأ اضغط تراجع وإرجاع الطلب للمحرر.</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
            <button onClick={() => setLastEditorSend(null)} style={{ ...btn, backgroundColor: '#27ae60', padding: '7px 12px' }}>الإبقاء على الإرسال</button>
            <button onClick={() => undoEditorSend(lastEditorSend)} style={{ ...btn, backgroundColor: '#e67e22', padding: '7px 12px' }}>تراجع وإرجاع للمحرر</button>
          </div>
        </div>
      )}
      <div className="no-print" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10, marginBottom: 15 }}>
        <input placeholder="بحث: اسم / مدني / ملف" value={filters.q} onChange={(e) => set('q', e.target.value)} style={inputStyle} />
        <select value={filters.type} onChange={(e) => set('type', e.target.value)} style={inputStyle}><option value="">كل الأنواع</option><option value="vacation">إجازة دورية</option><option value="emergency">عارضة</option><option value="extension">تمديد</option><option value="returnToWork">مباشرة</option></select>
        <select value={filters.status} onChange={(e) => set('status', e.target.value)} style={inputStyle}><option value="">كل الحالات</option><option value="pending_shift_supervisor">بانتظار مسؤول النوبة</option><option value="late_shift_supervisor">متأخر لدى مسؤول النوبة</option><option value="pending_editor_review">بانتظار المحرر</option><option value="pending_manager">بانتظار المراقب</option><option value="approved">معتمد نهائيًا</option><option value="rejected_by_shift_supervisor">مرفوض من مسؤول النوبة</option><option value="rejected_by_manager">مرفوض من المراقب</option><option value="cancelled_by_employee">ملغى من الموظف</option></select>
        <select value={filters.shift} onChange={(e) => set('shift', e.target.value)} style={inputStyle}><option value="">كل النوبات</option>{['A','B','C','D','F','M'].map(x => <option key={x} value={x}>{x}</option>)}</select>
        <select value={filters.workLocation} onChange={(e) => set('workLocation', e.target.value)} style={inputStyle}><option value="">كل أماكن العمل</option>{WORK_LOCATIONS.map(x => <option key={x} value={x}>{x}</option>)}</select>
        <input type="date" dir="ltr" lang="en-CA" value={filters.from} onChange={(e) => set('from', e.target.value)} style={dateInputStyle} />
        <input type="date" dir="ltr" lang="en-CA" value={filters.to} onChange={(e) => set('to', e.target.value)} style={dateInputStyle} />
        <button onClick={load} style={{ ...btn, backgroundColor: '#3498db', marginBottom: 10 }}>تطبيق الفلتر</button>
      </div>
      {canEditorSend && editorEligibleIds.length > 0 && (
        <div className="no-print" style={{ background: '#f4f8fb', border: '1px solid #c8d9e6', borderRadius: 8, padding: 12, marginBottom: 15 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontWeight: 'bold', cursor: 'pointer' }}>
              <input type="checkbox" checked={allEditorEligibleSelected} onChange={(event) => toggleAllEditorEligible(event.target.checked)} />
              تحديد كل الطلبات الظاهرة والمؤهلة / Select all visible eligible requests
            </label>
            <span style={{ color: '#34495e', fontWeight: 'bold' }}>المحدد / Selected: {selectedVisibleEditorIds.length}</span>
            <button disabled={bulkEditorProcessing || selectedVisibleEditorIds.length === 0} onClick={sendSelectedToManager} style={{ ...btn, backgroundColor: '#27ae60', padding: '7px 12px', opacity: bulkEditorProcessing || selectedVisibleEditorIds.length === 0 ? 0.55 : 1 }}>
              {bulkEditorProcessing ? 'جاري التنفيذ... / Processing...' : 'إرسال الطلبات المحددة للمراقب / Send Selected to Manager'}
            </button>
          </div>
          <div style={{ color: '#666', marginTop: 8, fontSize: 13 }}>يشمل التحديد الجماعي الطلبات التي وصلت إلى مرحلة المحرر فقط؛ تجاوز مسؤول النوبة يبقى إجراءً فرديًا بسبب إلزامية السبب. / Bulk send includes editor-review requests only; supervisor bypass remains individual because it requires a reason.</div>
        </div>
      )}
      {bulkEditorResult && (
        <div style={{ background: bulkEditorResult.failureCount > 0 ? '#fff8e1' : '#eafaf1', border: `1px solid ${bulkEditorResult.failureCount > 0 ? '#f39c12' : '#27ae60'}`, color: '#34495e', padding: 12, borderRadius: 8, marginBottom: 15, lineHeight: 1.7 }}>
          <strong>نجح / Succeeded: {bulkEditorResult.successCount || 0} — فشل / Failed: {bulkEditorResult.failureCount || 0}</strong>
          {(bulkEditorResult.failures || []).length > 0 && (
            <ul style={{ marginBottom: 0 }}>
              {bulkEditorResult.failures.map(item => <li key={item.id}>{rows.find(row => row.id === item.id)?.employeeName || item.id}: {item.error}</li>)}
            </ul>
          )}
        </div>
      )}

      {loading ? <p>جاري التحميل...</p> : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', minWidth: 1300, borderCollapse: 'collapse' }}>
            <thead><tr>{['اختيار','الموظف','المدني','الملف','المسمى','النوبة','مكان العمل','النوع','من','إلى','الأيام','تاريخ التقديم','الحالة','مسؤول النوبة','المراقب','تفاصيل / إجراء'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
            <tbody>{rows.map(r => (
              <React.Fragment key={r.id}>
                <tr>
                  <td style={td}>{canEditorSend && r.workflowStatus === 'pending_editor_review' ? <input aria-label={`اختيار ${r.employeeName || r.id}`} type="checkbox" checked={selectedEditorIds.includes(r.id)} onChange={(event) => toggleEditorSelected(r.id, event.target.checked)} /> : '-'}</td><td style={td}>{r.employeeName || '-'}</td><td style={td}>{r.civilID || '-'}</td><td style={td}>{r.fileNo || '-'}</td><td style={td}>{r.jobTitle || '-'}</td><td style={td}>{r.shift || '-'}</td><td style={td}>{r.workLocation || '-'}</td><td style={td}>{r.typeArabic || requestTypeLabel(r.type, 'ar')}</td><td style={td}>{englishDate(r.startDate)}</td><td style={td}>{englishDate(r.endDate)}</td><td style={td}>{r.days || '-'}</td><td style={td}>{englishDate(r.submittedAt || r.createdAt)}</td><td style={td}><span style={{ color:'white', background: statusBadge(r.workflowStatus), padding:'4px 8px', borderRadius:12, fontSize:12 }}>{statusForTracking(r)}</span></td><td style={td}>{r.editorBypassedShiftSupervisor ? 'تجاوز بواسطة المحرر' : (r.shiftSupervisorName || '-')}</td><td style={td}>{r.managerName || r.approvedByName || '-'}</td><td style={td}><div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>{canEditorSend && ['pending_editor_review','pending_shift_supervisor','late_shift_supervisor'].includes(r.workflowStatus) && <button onClick={() => sendToManager(r)} style={{ ...btn, backgroundColor: ['pending_shift_supervisor','late_shift_supervisor'].includes(r.workflowStatus) ? '#c0392b' : '#27ae60', padding:'6px 10px' }}>{['pending_shift_supervisor','late_shift_supervisor'].includes(r.workflowStatus) ? 'تجاوز مسؤول النوبة وإرسال للمراقب' : 'إرسال للمراقب'}</button>}<button onClick={() => editRequestDatesPrompt(r, load, 'ar')} style={{ ...btn, backgroundColor: '#f39c12', padding:'6px 10px' }}>تعديل التواريخ</button><button onClick={() => setExpanded(expanded === r.id ? '' : r.id)} style={{ ...btn, backgroundColor: '#7f8c8d', padding:'6px 10px' }}>{expanded === r.id ? 'إخفاء' : 'عرض'}</button></div></td>
                </tr>
                {expanded === r.id && <tr><td colSpan="16" style={{ ...td, backgroundColor:'#f8f9fa' }}>
                  <strong>البيان الكامل:</strong>
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(240px, 1fr))', gap:8, marginTop:10 }}>
                    <div>نوع الدوام: {r.shiftType || '-'}</div><div>اعتماد مسؤول النوبة: {r.editorBypassedShiftSupervisor ? 'تم التجاوز بواسطة المحرر' : (r.shiftSupervisorDecision || '-')}</div><div>تاريخ مسؤول النوبة: {englishDate(r.shiftSupervisorAt)}</div><div>ملاحظات مسؤول النوبة: {r.shiftSupervisorNotes || '-'}</div><div>تجاوز مسؤول النوبة بواسطة: {r.editorBypassedShiftSupervisorByName || '-'}</div><div>سبب تجاوز مسؤول النوبة: {r.editorBypassShiftSupervisorNotes || '-'}</div><div>مراجعة المحرر: {r.editorReviewedByName || '-'}</div><div>تاريخ مراجعة المحرر: {englishDate(r.editorReviewedAt)}</div><div>ملاحظات المحرر: {r.editorReviewNotes || '-'}</div><div>اعتماد المراقب: {r.managerDecision || '-'}</div><div>تاريخ المراقب: {englishDate(r.managerAt || r.approvedAt)}</div><div>ملاحظات المراقب: {r.managerNotes || r.approvalNotes || '-'}</div><div>ملاحظات الطلب: {r.notes || '-'}</div>
                  </div>
                  {(r.managerNotes || r.approvalNotes || r.rejectionNotes) && (
                    <div style={{ marginTop: 10, background: '#fff8e1', border: '1px solid #f1c40f', borderRadius: 8, padding: 10 }}>
                      <strong>ملاحظات المراقب:</strong> {r.managerNotes || r.approvalNotes || r.rejectionNotes}
                    </div>
                  )}
                  <div style={{ marginTop:10 }}><strong>Timeline:</strong><ul>{(r.timeline || []).map((x, i) => <li key={i}>{x.date || '-'} — {x.label} — {x.by || '-'}{x.notes ? ` — ملاحظات: ${x.notes}` : ''}</li>)}</ul></div>
                </td></tr>}
              </React.Fragment>
            ))}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function OnDutyTodayTab({ currentUser }) {
  const lang = currentUser?.language || 'ar';
  const isEnglish = lang === 'en';
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [shift, setShift] = useState('');
  const [workLocation, setWorkLocation] = useState('');
  const [data, setData] = useState({ onDuty: [], notAvailable: [], counts: { onDuty: 0, notAvailable: 0, total: 0 } });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const text = {
    title: isEnglish ? 'Daily Attendance Status' : 'كشف الحضور اليومي',
    date: isEnglish ? 'Date' : 'التاريخ',
    shift: isEnglish ? 'Shift' : 'النوبة',
    allShifts: isEnglish ? 'All shifts within my scope' : 'كل النوبات ضمن النطاق',
    workLocation: isEnglish ? 'Work Location' : 'مكان العمل',
    allLocations: isEnglish ? 'All work locations within my scope' : 'كل أماكن العمل ضمن النطاق',
    show: isEnglish ? 'Show' : 'عرض',
    loading: isEnglish ? 'Loading...' : 'جاري التحميل...',
    onDuty: isEnglish ? 'Present / On Duty Employees' : 'الموظفون الحاضرون / على رأس العمل',
    notAvailable: isEnglish ? 'Not Available / Has Request' : 'غير متواجدين / لديهم طلب',
    noOnDuty: isEnglish ? 'No on-duty employees found.' : 'لا يوجد موظفون مداومون حسب الفلاتر.',
    noUnavailable: isEnglish ? 'No unavailable employees found.' : 'لا يوجد موظفون غير متواجدين حسب الفلاتر.',
    employee: isEnglish ? 'Employee' : 'الموظف',
    fileNo: isEnglish ? 'File No.' : 'رقم الملف',
    civilID: isEnglish ? 'Civil ID' : 'الرقم المدني',
    jobTitle: isEnglish ? 'Job Title' : 'المسمى الوظيفي',
    scheduled: isEnglish ? 'Today Duty' : 'دوامه اليوم',
    reason: isEnglish ? 'Reason' : 'السبب',
    status: isEnglish ? 'Status' : 'الحالة',
    period: isEnglish ? 'Period' : 'الفترة',
    notes: isEnglish ? 'Notes' : 'ملاحظات',
    failed: isEnglish ? 'Unable to load on-duty list.' : 'تعذر تحميل قائمة المداومين',
    connection: isEnglish ? 'Cannot connect to server.' : 'تعذر الاتصال بالخادم'
  };

  const load = async () => {
    setLoading(true);
    setMessage('');
    try {
      const params = new URLSearchParams();
      if (date) params.set('date', date);
      if (shift) params.set('shift', shift);
      if (workLocation) params.set('workLocation', workLocation);
      const res = await apiFetch(`${API_URL}/on-duty-today?${params.toString()}`);
      const payload = await res.json();
      if (res.ok) setData(payload);
      else setMessage(payload.error || text.failed);
    } catch {
      setMessage(text.connection);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);
  const employeeHeaders = [text.employee, text.fileNo, text.civilID, text.shift, text.workLocation, text.jobTitle, text.scheduled];
  const unavailableHeaders = [text.employee, text.fileNo, text.shift, text.workLocation, text.scheduled, text.reason, text.status, text.period, text.notes];

  return (
    <div style={{ backgroundColor: 'white', padding: 25, borderRadius: 8 }} dir={isEnglish ? 'ltr' : 'rtl'}>
      <h2 style={{ marginTop: 0 }}>{text.title}</h2>
      <div className="no-print" style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'end', marginBottom: 18 }}>
        <label style={{ minWidth: 230 }}>
          <div style={{ fontWeight: 'bold', marginBottom: 6 }}>{text.date}</div>
          <DatePartsInput value={date} language={lang} required onChange={setDate} />
        </label>
        <label>
          <div style={{ fontWeight: 'bold', marginBottom: 6 }}>{text.shift}</div>
          <select value={shift} onChange={(e) => setShift(e.target.value)} style={{ ...inputStyle, marginBottom: 0, minWidth: 170 }}>
            <option value="">{text.allShifts}</option>
            {SHIFT_OPTIONS.map(s => <option key={s.value} value={s.value}>{isEnglish ? displayShiftValue(s.value, 'en') : s.label}</option>)}
          </select>
        </label>
        <label>
          <div style={{ fontWeight: 'bold', marginBottom: 6 }}>{text.workLocation}</div>
          <select value={workLocation} onChange={(e) => setWorkLocation(e.target.value)} style={{ ...inputStyle, marginBottom: 0, minWidth: 220 }}>
            <option value="">{text.allLocations}</option>
            {WORK_LOCATIONS.map(x => <option key={x} value={x}>{displayWorkLocation(x, lang)}</option>)}
          </select>
        </label>
        <button onClick={load} style={{ ...btn, backgroundColor: '#3498db', padding: '10px 18px' }}>{text.show}</button>
      </div>

      {message && <div style={{ background: '#fdecea', color: '#c0392b', border: '1px solid #e74c3c', padding: 10, borderRadius: 6, marginBottom: 12 }}>{message}</div>}
      {loading ? <p>{text.loading}</p> : (
        <>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 15 }}>
            <span style={{ background: '#eafaf1', color: '#1e8449', padding: '6px 10px', borderRadius: 12, fontWeight: 'bold' }}>{text.onDuty}: {data.counts?.onDuty || 0}</span>
            <span style={{ background: '#fff3cd', color: '#856404', padding: '6px 10px', borderRadius: 12, fontWeight: 'bold' }}>{text.notAvailable}: {data.counts?.notAvailable || 0}</span>
          </div>

          <h3>{text.onDuty}</h3>
          {(data.onDuty || []).length === 0 ? <p style={{ color: '#666' }}>{text.noOnDuty}</p> : (
            <Table headers={employeeHeaders}>
              {(data.onDuty || []).map(r => (
                <tr key={r.id}>
                  <td style={td}>{r.employeeName}</td>
                  <td style={td}>{r.fileNo || '-'}</td>
                  <td style={td}>{r.civilID || '-'}</td>
                  <td style={td}>{displayShiftValue(r.shift, lang)}</td>
                  <td style={td}>{displayWorkLocation(r.workLocation, lang)}</td>
                  <td style={td}>{displayJobTitle(r.jobTitle, lang)}</td>
                  <td style={td}>{r.scheduledStatusLabel || '-'}</td>
                </tr>
              ))}
            </Table>
          )}

          <h3 style={{ marginTop: 25 }}>{text.notAvailable}</h3>
          {(data.notAvailable || []).length === 0 ? <p style={{ color: '#666' }}>{text.noUnavailable}</p> : (
            <Table headers={unavailableHeaders}>
              {(data.notAvailable || []).map((r, i) => (
                <tr key={`${r.id}-${i}`}>
                  <td style={td}>{r.employeeName}</td>
                  <td style={td}>{r.fileNo || '-'}</td>
                  <td style={td}>{displayShiftValue(r.shift, lang)}</td>
                  <td style={td}>{displayWorkLocation(r.workLocation, lang)}</td>
                  <td style={td}>{r.scheduledStatusLabel || '-'}</td>
                  <td style={td}>{r.reason || '-'}</td>
                  <td style={td}>{r.requestStatus || '-'}</td>
                  <td style={td}>{r.period || '-'}</td>
                  <td style={td}>{r.notes || '-'}</td>
                </tr>
              ))}
            </Table>
          )}
        </>
      )}
    </div>
  );
}


async function editRequestDatesPrompt(row, onDone, language = 'ar') {
  const isEnglish = language === 'en';
  const type = normalizeLeaveType(row.type);
  const labels = {
    reason: isEnglish ? 'Reason for date correction (internal log only):' : 'سبب تعديل التاريخ (يظهر في السجل الداخلي فقط):',
    start: isEnglish ? 'Correct start date (YYYY-MM-DD):' : 'تاريخ بداية الإجازة الصحيح (YYYY-MM-DD):',
    end: isEnglish ? 'Correct end date (YYYY-MM-DD):' : 'تاريخ نهاية الإجازة الصحيح (YYYY-MM-DD):',
    returnLeaveStart: isEnglish ? 'Correct source leave start date (YYYY-MM-DD):' : 'تاريخ بداية الإجازة المرتبطة بالمباشرة (YYYY-MM-DD):',
    returnLeaveEnd: isEnglish ? 'Correct source leave end date (YYYY-MM-DD):' : 'تاريخ نهاية الإجازة المرتبطة بالمباشرة (YYYY-MM-DD):',
    emergency: isEnglish ? 'Correct emergency leave date (YYYY-MM-DD):' : 'تاريخ العارضة الصحيح (YYYY-MM-DD):',
    returnDate: isEnglish ? 'Correct return-to-work date (YYYY-MM-DD):' : 'تاريخ المباشرة الصحيح (YYYY-MM-DD):',
    cancelled: isEnglish ? 'Date edit cancelled.' : 'تم إلغاء تعديل التاريخ.',
    failed: isEnglish ? 'Unable to update request dates.' : 'تعذر تعديل تواريخ الطلب.',
    done: isEnglish ? 'Request dates updated successfully.' : 'تم تعديل تواريخ الطلب بنجاح.',
    invalid: isEnglish ? 'Invalid date. Use YYYY-MM-DD.' : 'التاريخ غير صحيح. استخدم الصيغة YYYY-MM-DD.'
  };
  const reason = window.prompt(labels.reason, '') || '';
  const body = { reason };
  const iso = (v) => /^\d{4}-\d{2}-\d{2}$/.test(String(v || '').trim());

  if (type === 'returnToWork') {
    const defaultLeaveStart = row.sourceLeaveStartDate || row.originalLeaveStartDate || row.startDate || '';
    const defaultLeaveEnd = row.sourceLeaveEndDate || row.originalLeaveEndDate || row.endDate || defaultLeaveStart || '';
    const leaveStart = window.prompt(labels.returnLeaveStart, defaultLeaveStart);
    if (leaveStart === null) return;
    if (!iso(leaveStart)) return alert(labels.invalid);
    const leaveEnd = window.prompt(labels.returnLeaveEnd, defaultLeaveEnd);
    if (leaveEnd === null) return;
    if (!iso(leaveEnd)) return alert(labels.invalid);
    const returnDate = window.prompt(labels.returnDate, row.returnToWorkDate || '');
    if (returnDate === null) return;
    if (!iso(returnDate)) return alert(labels.invalid);
    body.startDate = leaveStart.trim();
    body.endDate = leaveEnd.trim();
    body.returnToWorkDate = returnDate.trim();
  } else if (type === 'emergency') {
    const date = window.prompt(labels.emergency, row.startDate || '');
    if (date === null) return;
    if (!iso(date)) return alert(labels.invalid);
    body.startDate = date.trim();
  } else {
    const startDate = window.prompt(labels.start, row.startDate || '');
    if (startDate === null) return;
    if (!iso(startDate)) return alert(labels.invalid);
    const endDate = window.prompt(labels.end, row.endDate || startDate || '');
    if (endDate === null) return;
    if (!iso(endDate)) return alert(labels.invalid);
    body.startDate = startDate.trim();
    body.endDate = endDate.trim();
  }

  try {
    const res = await apiFetch(`${API_URL}/employee-requests/${row.id}/dates`, { method: 'PATCH', body: JSON.stringify(body) });
    const data = await res.json();
    alert(res.ok ? (data.message || labels.done) : (data.error || labels.failed));
    if (res.ok && typeof onDone === 'function') onDone();
  } catch {
    alert(labels.failed);
  }
}

function RequestsDecisionTable({ endpoint, title, approvePath, bulkApprovePath, rejectPath, language = 'ar', mode = 'default', allowDateEdit = false, onDecisionDone }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastDecision, setLastDecision] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const isEnglish = language === 'en';
  const isSupervisorMode = mode === 'supervisor';
  const t = {
    title: isEnglish && isSupervisorMode ? 'Shift Supervisor Approval' : title,
    refresh: isEnglish ? 'Refresh' : 'تحديث',
    loading: isEnglish ? 'Loading...' : 'جاري التحميل...',
    noRequests: isEnglish ? 'No requests within your current scope.' : 'لا توجد طلبات ضمن نطاقك الحالي.',
    loadFailed: isEnglish ? 'Unable to load requests.' : 'تعذر تحميل الطلبات',
    connectionFailed: isEnglish ? 'Cannot connect to server.' : 'تعذر الاتصال بالخادم',
    approvePrompt: isEnglish ? 'Approval notes (optional):' : 'ملاحظات الاعتماد (اختياري):',
    rejectPrompt: isEnglish ? 'Rejection reason:' : 'سبب الرفض:',
    done: isEnglish ? 'Done' : 'تم',
    failed: isEnglish ? 'Unable to complete the action' : 'تعذر تنفيذ العملية',
    employeeNotes: isEnglish ? 'Employee Notes' : 'ملاحظات الموظف',
    supervisorNotes: isEnglish ? 'Shift Supervisor Notes' : 'ملاحظات مسؤول النوبة',
    editorNotes: isEnglish ? 'Editor Notes' : 'ملاحظات المحرر',
    managerNotes: isEnglish ? 'Manager Notes' : 'ملاحظات المراقب',
    select: isEnglish ? 'Select' : 'اختيار',
    selectAll: isEnglish ? 'Select all visible requests' : 'تحديد كل الطلبات الظاهرة',
    selectedCount: isEnglish ? 'Selected' : 'المحدد',
    approveSelected: isEnglish ? 'Approve Selected' : 'اعتماد الطلبات المحددة',
    bulkNotesPrompt: isEnglish ? 'Shared approval notes for the selected requests (optional):' : 'ملاحظات اعتماد مشتركة للطلبات المحددة (اختياري):',
    noSelection: isEnglish ? 'Select at least one request.' : 'حدد طلبًا واحدًا على الأقل.',
    processing: isEnglish ? 'Processing...' : 'جاري التنفيذ...',
    succeeded: isEnglish ? 'Succeeded' : 'نجح',
    failedCount: isEnglish ? 'Failed' : 'فشل',
    approve: isEnglish ? 'Approve' : 'اعتماد',
    reject: isEnglish ? 'Reject' : 'رفض',
    keepDecision: isEnglish ? 'Keep decision' : 'الإبقاء على القرار',
    undoDecision: isEnglish ? 'Undo this decision' : 'التراجع عن هذا القرار',
    undoDone: isEnglish ? 'Decision was undone.' : 'تم التراجع عن القرار.',
    undoHelpSupervisor: isEnglish
      ? 'The request was approved by the shift supervisor and sent to the editor. Use Undo only if this approval was made by mistake.'
      : 'تم اعتماد الطلب من مسؤول النوبة وإرساله للمحرر. استخدم زر التراجع فقط إذا كان الاعتماد بالخطأ.',
    undoHelpManager: isEnglish
      ? 'The request was finally approved by the manager. Use Undo only if the final approval was made by mistake.'
      : 'تم الاعتماد النهائي من المراقب. استخدم زر التراجع فقط إذا كان الاعتماد النهائي بالخطأ.',
    editDates: isEnglish ? 'Edit Dates' : 'تعديل التواريخ',
    headers: isEnglish
      ? ['Select', 'Employee', 'File No.', 'Shift', 'Work Location', 'Request Type', 'From', 'To', 'Days', 'Status', 'Notes', 'Actions']
      : ['اختيار', 'الموظف', 'رقم الملف', 'النوبة', 'مكان العمل', 'نوع الطلب', 'من', 'إلى', 'الأيام', 'الحالة', 'ملاحظات', 'إجراءات']
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await apiFetch(`${API_URL}/${endpoint}`);
      const data = await res.json();
      if (res.ok) {
        const nextRows = data.requests || [];
        setRows(nextRows);
        setSelectedIds(previous => previous.filter(id => nextRows.some(row => row.id === id)));
      }
      else alert(data.error || t.loadFailed);
    } catch { alert(t.connectionFailed); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);
  const visibleIds = rows.map(row => row.id);
  const selectedVisibleIds = visibleIds.filter(id => selectedIds.includes(id));
  const allVisibleSelected = visibleIds.length > 0 && selectedVisibleIds.length === visibleIds.length;

  const toggleSelectAll = (checked) => {
    setSelectedIds(checked ? visibleIds : []);
  };

  const toggleSelected = (id, checked) => {
    setSelectedIds(previous => checked ? [...new Set([...previous, id])] : previous.filter(value => value !== id));
  };

  const approveSelected = async () => {
    if (selectedVisibleIds.length === 0) {
      alert(t.noSelection);
      return;
    }
    const notes = window.prompt(t.bulkNotesPrompt, '');
    if (notes === null) return;
    const count = selectedVisibleIds.length;
    const confirmed = window.confirm(`سيتم اعتماد ${count} طلبًا محددًا. هل تريد المتابعة؟\n${count} selected request(s) will be approved. Continue?`);
    if (!confirmed) return;
    setBulkProcessing(true);
    try {
      const res = await apiFetch(`${API_URL}/employee-requests/${bulkApprovePath}`, {
        method: 'POST',
        body: JSON.stringify({ requestIds: selectedVisibleIds, notes })
      });
      const data = await res.json();
      if (!res.ok) {
        alert(data.error || t.failed);
        return;
      }
      setBulkResult(data);
      setSelectedIds((data.failures || []).map(item => item.id));
      alert(`${t.succeeded}: ${data.successCount || 0} — ${t.failedCount}: ${data.failureCount || 0}`);
      await load();
      if (typeof onDecisionDone === 'function') onDecisionDone();
    } catch {
      alert(t.connectionFailed);
    } finally {
      setBulkProcessing(false);
    }
  };

  const undoDecision = async (decision) => {
    if (!decision?.id) return;
    try {
      const undoRes = await apiFetch(`${API_URL}/employee-requests/${decision.id}/undo-last-decision`, { method: 'POST' });
      const undoData = await undoRes.json();
      alert(undoRes.ok ? (undoData.message || t.undoDone) : (undoData.error || t.failed));
      if (undoRes.ok) setLastDecision(null);
      load();
      if (typeof onDecisionDone === 'function') onDecisionDone();
    } catch {
      alert(t.connectionFailed);
    }
  };

  const decide = async (row, action) => {
    const notes = window.prompt(action === 'approve' ? t.approvePrompt : t.rejectPrompt, '') || '';
    if (action === 'reject' && !notes) return;
    if (action === 'reject') {
      const ok = window.confirm(isEnglish
        ? 'This will reject the request. Press OK to reject, or Cancel to keep it pending.'
        : 'سيتم رفض الطلب. اضغط موافق للرفض، أو إلغاء لإبقائه بانتظار الاعتماد.');
      if (!ok) return;
    }
    const path = action === 'approve' ? approvePath : rejectPath;
    const res = await apiFetch(`${API_URL}/employee-requests/${row.id}/${path}`, { method: 'POST', body: JSON.stringify({ notes }) });
    const data = await res.json();
    if (!res.ok) {
      alert(data.error || t.failed);
      return;
    }
    if (action === 'approve') {
      setLastDecision({ id: row.id, employeeName: row.employeeName || '', message: data.message || t.done, help: isSupervisorMode ? t.undoHelpSupervisor : t.undoHelpManager });
    } else {
      setLastDecision(null);
      alert(data.message || t.done);
    }
    load();
    if (typeof onDecisionDone === 'function') onDecisionDone();
  };

  return (
    <div style={{ backgroundColor: 'white', padding: 25, borderRadius: 8 }} dir={isEnglish ? 'ltr' : 'rtl'}>
      <h2 style={{ marginTop: 0 }}>{t.title}</h2>
      <button onClick={load} style={{ ...btn, backgroundColor: '#3498db', marginBottom: 15 }}>{t.refresh}</button>
      {rows.length > 0 && (
        <div className="no-print" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', background: '#f4f8fb', border: '1px solid #c8d9e6', borderRadius: 8, padding: 12, marginBottom: 15 }}>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontWeight: 'bold', cursor: 'pointer' }}>
            <input type="checkbox" checked={allVisibleSelected} onChange={(event) => toggleSelectAll(event.target.checked)} />
            {t.selectAll}
          </label>
          <span style={{ color: '#34495e', fontWeight: 'bold' }}>{t.selectedCount}: {selectedVisibleIds.length}</span>
          <button disabled={bulkProcessing || selectedVisibleIds.length === 0} onClick={approveSelected} style={{ ...btn, backgroundColor: '#27ae60', padding: '7px 12px', opacity: bulkProcessing || selectedVisibleIds.length === 0 ? 0.55 : 1 }}>
            {bulkProcessing ? t.processing : t.approveSelected}
          </button>
        </div>
      )}
      {bulkResult && (
        <div style={{ background: bulkResult.failureCount > 0 ? '#fff8e1' : '#eafaf1', border: `1px solid ${bulkResult.failureCount > 0 ? '#f39c12' : '#27ae60'}`, color: '#34495e', padding: 12, borderRadius: 8, marginBottom: 15, lineHeight: 1.7 }}>
          <strong>{t.succeeded}: {bulkResult.successCount || 0} — {t.failedCount}: {bulkResult.failureCount || 0}</strong>
          {(bulkResult.failures || []).length > 0 && (
            <ul style={{ marginBottom: 0 }}>
              {bulkResult.failures.map(item => <li key={item.id}>{rows.find(row => row.id === item.id)?.employeeName || item.id}: {item.error}</li>)}
            </ul>
          )}
        </div>
      )}
      {lastDecision && (
        <div style={{ background: '#fff8e1', border: '1px solid #f39c12', color: '#7a4f01', padding: 12, borderRadius: 8, marginBottom: 15, lineHeight: 1.7 }}>
          <div style={{ fontWeight: 'bold' }}>{lastDecision.message}</div>
          <div>{lastDecision.help}</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
            <button onClick={() => setLastDecision(null)} style={{ ...btn, backgroundColor: '#27ae60', padding: '7px 12px' }}>{t.keepDecision}</button>
            <button onClick={() => undoDecision(lastDecision)} style={{ ...btn, backgroundColor: '#e67e22', padding: '7px 12px' }}>{t.undoDecision}</button>
          </div>
        </div>
      )}
      {loading ? <p>{t.loading}</p> : rows.length === 0 ? <p style={{ color: '#666', fontWeight: 'bold' }}>{t.noRequests}</p> : (
        <Table headers={t.headers}>
          {rows.map(r => (
            <tr key={r.id}>
              <td style={td}><input aria-label={`${t.select} ${r.employeeName || r.id}`} type="checkbox" checked={selectedIds.includes(r.id)} onChange={(event) => toggleSelected(r.id, event.target.checked)} /></td>
              <td style={td}>{r.employeeName}</td>
              <td style={td}>{r.fileNo || '-'}</td>
              <td style={td}>{displayShiftValue(r.shift, language)}</td>
              <td style={td}>{displayWorkLocation(r.workLocation, language)}</td>
              <td style={td}>{isEnglish ? requestTypeLabel(r.type, 'en') : (r.typeArabic || TYPE_LABELS[r.type] || r.type)}</td>
              <td style={td}>{englishDate(r.startDate)}</td>
              <td style={td}>{englishDate(r.endDate)}</td>
              <td style={td}>{r.days || '-'}</td>
              <td style={td}><span style={{ color: 'white', background: statusBadge(r.workflowStatus), padding: '4px 8px', borderRadius: 12, fontSize: 12 }}>{requestWorkflowLabel(r.workflowStatus, language)}</span></td>
              <td style={td}>
                <div><strong>{t.employeeNotes}:</strong> {r.notes || '-'}</div>
                <div><strong>{t.supervisorNotes}:</strong> {r.shiftSupervisorNotes || '-'}</div>
                <div><strong>{t.editorNotes}:</strong> {r.editorReviewNotes || '-'}</div>
                <div><strong>{t.managerNotes}:</strong> {r.managerNotes || r.approvalNotes || r.rejectionNotes || '-'}</div>
              </td>
              <td style={td}>
                {allowDateEdit && <button onClick={() => editRequestDatesPrompt(r, load, language)} style={{ ...btn, backgroundColor: '#f39c12', padding: '6px 10px', marginInlineEnd: 6 }}>{t.editDates}</button>}
                <button onClick={() => decide(r, 'approve')} style={{ ...btn, backgroundColor: '#27ae60', padding: '6px 10px', marginInlineEnd: 6 }}>{t.approve}</button>
                <button onClick={() => decide(r, 'reject')} style={{ ...btn, backgroundColor: '#e74c3c', padding: '6px 10px' }}>{t.reject}</button>
              </td>
            </tr>
          ))}
        </Table>
      )}
    </div>
  );
}

function SupervisorRequestsTab({ currentUser, onDecisionDone }) {
  const lang = currentUser?.language || 'ar';
  return <RequestsDecisionTable endpoint="supervisor-requests" title="طلبات بانتظار مسؤول النوبة" approvePath="supervisor-approve" bulkApprovePath="bulk/supervisor-approve" rejectPath="supervisor-reject" language={lang} mode="supervisor" onDecisionDone={onDecisionDone} />;
}

function ManagerRequestsTab({ currentUser, onDecisionDone }) {
  const lang = currentUser?.language || 'ar';
  return <RequestsDecisionTable endpoint="manager-requests" title={lang === 'en' ? 'Manager Final Approval' : 'طلبات بانتظار الاعتماد النهائي من المراقب'} approvePath="manager-approve" bulkApprovePath="bulk/manager-approve" rejectPath="manager-reject" language={lang} allowDateEdit={true} onDecisionDone={onDecisionDone} />;
}

function Table({ headers, children }) {
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr>
          {headers.map(h => <th key={h} style={th}>{h}</th>)}
        </tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  );
}

function ReportTab({ reportShift, setReportShift, fetchReport, reportLoading, reportData }) {
  return (
    <div>
      <div style={{ display: 'flex', gap: '15px', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap' }} className="no-print">
        <label style={{ fontWeight: 'bold' }}>اختر النوبة:</label>
        <select value={reportShift} onChange={(e) => { setReportShift(e.target.value); fetchReport(e.target.value); }} style={{ padding: '10px 20px', border: '2px solid #667eea', borderRadius: '6px', fontSize: '15px', cursor: 'pointer' }}>
          <option value="all">جميع النوبات</option>
          {SHIFT_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
        </select>
        <button onClick={() => window.print()} style={{ ...btn, backgroundColor: '#e74c3c', display: 'flex', alignItems: 'center', gap: '8px' }}><Printer size={18} /> طباعة التقرير</button>
        <button onClick={() => fetchReport()} style={{ ...btn, backgroundColor: '#3498db', display: 'flex', alignItems: 'center', gap: '8px' }}><RefreshCw size={16} /> تحديث</button>
      </div>

      <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '8px', overflowX: 'auto' }}>
        <OfficialHeader
          title={`التقرير السنوي ${new Date().getFullYear()}`}
          subtitle={reportShift !== 'all' ? `نوبة ${reportShift}` : 'جميع النوبات'}
        />
        <h2 className="no-print" style={{ color: '#2c3e50', marginTop: 0 }}>
          التقرير السنوي {new Date().getFullYear()}
          {reportShift !== 'all' ? ` — نوبة ${reportShift}` : ' — جميع النوبات'}
          {' '}({reportData.length} موظف)
        </h2>

        {reportLoading ? <p style={{ textAlign: 'center', padding: '30px' }}>جاري تحميل التقرير...</p> : (
          <Table headers={['الاسم', 'الرقم المدني', 'رقم الملف', 'النوبة', 'مكان العمل', 'أيام العمل الفعلية', 'أيام الدوام المتأثرة', 'دورية', 'عارضة', 'مرضية', 'غياب', 'مهمة', 'دورة', 'راحة', 'بدون راتب', 'تكليف', 'إجمالي أيام الإجازات']}>
            {reportData.map(emp => (
              <tr key={emp.id}>
                <td style={td}>{emp.fullName}</td>
                <td style={td}>{emp.civilID}</td>
                <td style={td}>{emp.fileNo}</td>
                <td style={td}>{(emp.shiftType === 'صباحي' || emp.shift === 'M') ? 'صباحي' : emp.shift}</td>
                <td style={td}>{emp.workLocation || '-'}</td>
                <td style={{ ...td, fontWeight: 'bold' }}>{emp.workDays}</td>
                <td style={{ ...td, fontWeight: 'bold' }}>{emp.workImpactDays || '-'}</td>
                <td style={td}>{emp.vacation || '-'}</td>
                <td style={td}>{emp.emergency || '-'}</td>
                <td style={td}>{emp.sick || '-'}</td>
                <td style={td}>{emp.absence || '-'}</td>
                <td style={td}>{emp.mission || '-'}</td>
                <td style={td}>{emp.training || '-'}</td>
                <td style={td}>{emp.rest || '-'}</td>
                <td style={td}>{emp.unpaid || '-'}</td>
                <td style={td}>{emp.assignment || '-'}</td>
                <td style={{ ...td, fontWeight: 'bold' }}>{emp.totalLeaveDays || '-'}</td>
              </tr>
            ))}
          </Table>
        )}
      </div>
    </div>
  );
}

function LocationReportTab({ filters, setFilters, fetchReport, loading, rows }) {
  return (
    <div>
      <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', marginBottom: '20px', display: 'flex', gap: '15px', alignItems: 'end', flexWrap: 'wrap' }} className="no-print">
        <div>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>مكان العمل:</label>
          <select value={filters.workLocation} onChange={(e) => setFilters({ ...filters, workLocation: e.target.value })} style={{ padding: '10px', minWidth: '180px' }}>
            <option value="all">جميع الأماكن</option>
            {WORK_LOCATIONS.map(item => <option key={item} value={item}>{item}</option>)}
          </select>
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>النوبة:</label>
          <select value={filters.shift} onChange={(e) => setFilters({ ...filters, shift: e.target.value })} style={{ padding: '10px', minWidth: '150px' }}>
            <option value="all">جميع النوبات</option>
            {SHIFT_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
          </select>
        </div>

        <button onClick={fetchReport} style={{ ...btn, backgroundColor: '#3498db' }}>عرض الكشف</button>
        <button onClick={() => window.print()} style={{ ...btn, backgroundColor: '#e74c3c' }}>طباعة الكشف</button>
      </div>

      <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '8px', overflowX: 'auto' }}>
        <OfficialHeader
          title="كشف أماكن العمل"
          subtitle={`${filters.workLocation !== 'all' ? filters.workLocation : 'جميع الأماكن'}${filters.shift !== 'all' ? ` — نوبة ${filters.shift}` : ''}`}
        />
        <h2 className="no-print" style={{ marginTop: 0, color: '#2c3e50' }}>كشف أماكن العمل ({rows.length} موظف)</h2>
        {loading ? <p style={{ textAlign: 'center', padding: '30px' }}>جاري التحميل...</p> : (
          <Table headers={['الاسم', 'الرقم المدني', 'رقم الملف', 'النوبة', 'مكان العمل']}>
            {rows.map(emp => (
              <tr key={emp.id}>
                <td style={td}>{emp.fullName}</td>
                <td style={td}>{emp.civilID}</td>
                <td style={td}>{emp.fileNo}</td>
                <td style={td}>{(emp.shiftType === 'صباحي' || emp.shift === 'M') ? 'صباحي' : emp.shift}</td>
                <td style={td}>{emp.workLocation || '-'}</td>
              </tr>
            ))}
          </Table>
        )}
      </div>
    </div>
  );
}

function LeaveStatementTab({
  employees,
  fetchEmployees,
  statementSearch,
  setStatementSearch,
  statementEmployeeList,
  selectedIds,
  toggleEmployee,
  selectAll,
  clearSelection,
  filters,
  setFilters,
  fetchStatement,
  loading,
  rows
}) {
  return (
    <div>
      <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', marginBottom: '20px' }} className="no-print">
        <h3 style={{ marginTop: 0 }}>بيان الإجازات</h3>
        <p style={{ color: '#666', marginTop: 0 }}>
          البيان يعرض الإجازات المعتمدة نهائيًا. البحث يكون حسب فترة تاريخ الاعتماد، أو الاسم، أو الرقم المدني، أو رقم الملف، أو نوع الإجازة. جميع الفلاتر اختيارية.
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '15px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '5px' }}>بحث موظف:</label>
            <input type="text" placeholder="اسم / مدني / ملف" value={statementSearch} onChange={(e) => setStatementSearch(e.target.value)} style={inputStyle} />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '5px' }}>من تاريخ الاعتماد:</label>
            <input type="date" dir="ltr" lang="en-CA" value={filters.approvalFromDate || ''} onChange={(e) => setFilters({ ...filters, approvalFromDate: e.target.value })} style={dateInputStyle} />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '5px' }}>إلى تاريخ الاعتماد:</label>
            <input type="date" dir="ltr" lang="en-CA" value={filters.approvalToDate || ''} onChange={(e) => setFilters({ ...filters, approvalToDate: e.target.value })} style={dateInputStyle} />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '5px' }}>نوع الإجازة:</label>
            <select value={filters.type} onChange={(e) => setFilters({ ...filters, type: e.target.value })} style={inputStyle}>
              <option value="all">الكل</option>
              {Object.entries(TYPE_LABELS).filter(([key]) => key !== 'returnToWork').map(([key, label]) => <option key={key} value={key}>{label}</option>)}
            </select>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '10px', marginBottom: '15px', flexWrap: 'wrap' }}>
          <button onClick={fetchStatement} style={{ ...btn, backgroundColor: '#27ae60' }}>عرض البيان</button>
          <button onClick={() => { setStatementSearch(''); setFilters({ approvalFromDate: '', approvalToDate: '', type: 'all' }); }} style={{ ...btn, backgroundColor: '#7f8c8d' }}>مسح البحث</button>
          <button onClick={() => window.print()} style={{ ...btn, backgroundColor: '#e74c3c' }}>طباعة البيان</button>
        </div>

        <p style={{ color: '#666' }}>كل فلاتر البحث اختيارية. إذا تريد يوم اعتماد واحد، اختر نفس التاريخ في خانتي من/إلى.</p>
      </div>

      <div className="statement-print-shell print-target" style={{ backgroundColor: 'white', padding: '25px', borderRadius: '8px', overflowX: 'auto' }}>
        <OfficialHeader
          title="بيان الإجازات"
          subtitle={(filters.approvalFromDate || filters.approvalToDate) ? `فترة الاعتماد: ${filters.approvalFromDate || 'البداية'} إلى ${filters.approvalToDate || 'النهاية'}` : undefined}
        />
        {loading ? <p style={{ textAlign: 'center', padding: '30px' }}>جاري التحميل...</p> : rows.length === 0 ? (
          <p style={{ textAlign: 'center', color: '#999', padding: '25px' }}>لا توجد بيانات للعرض</p>
        ) : (
          <>
            <style>{`
              .leave-statement-table th, .leave-statement-table td { border: 1px solid #34495e !important; text-align: center !important; background: #fff !important; font-size: 13px !important; font-weight: 700 !important; }
              .leave-statement-table th { color: #0b1f4d; font-weight: 700 !important; font-size: 13.5px !important; }
              .leave-statement-table .name-col { width: 1% !important; min-width: 115px !important; white-space: nowrap !important; font-weight: 700 !important; font-size: 13px !important; }
              .leave-statement-table .print-only { display: none; }
              .leave-statement-table .manual-note-col { min-width: 155px; width: 18%; }
              .statement-footer { margin-top: 35px; width: 220px; float: left; clear: both; text-align: center; color: #0b1f4d; font-weight: 700; font-size: 16px; direction: rtl; }
              .statement-footer-line { border-top: 1.5px solid #111; margin-top: 18px; }
              @media print {
                @page { size: A4 landscape; margin: 8mm; @bottom-center { content: counter(page); font-size: 10px; color: #111; } }
                html, body, #root { margin: 0 !important; padding: 0 !important; height: auto !important; overflow: visible !important; background: #fff !important; }
                .no-print { display: none !important; }
                .print-target { display: block !important; position: static !important; width: 100% !important; margin: 0 !important; padding: 0 !important; border-radius: 0 !important; overflow: visible !important; background: #fff !important; box-shadow: none !important; page-break-after: avoid !important; break-after: avoid-page !important; }
                .leave-statement-table table { font-size: 12.5px !important; table-layout: auto !important; width: 100% !important; }
                .leave-statement-table .print-hide-column { display: none !important; }
                .leave-statement-table .print-only { display: table-cell !important; }
                .leave-statement-table .manual-note-col { min-width: 150px !important; width: 18% !important; height: 30px !important; white-space: normal !important; }
                .leave-statement-table .name-col { width: 1% !important; min-width: 115px !important; white-space: nowrap !important; font-weight: 700 !important; font-size: 13px !important; }
                .leave-statement-table th, .leave-statement-table td { padding: 5px 4px !important; background: #fff !important; color: #0b1f4d !important; border: 1px solid #1d3557 !important; font-size: 13px !important; font-weight: 700 !important; line-height: 1.35 !important; white-space: nowrap !important; }
                .statement-footer { margin-top: 35px !important; float: left !important; clear: both !important; margin-left: 0 !important; margin-right: auto !important; page-break-inside: avoid !important; break-inside: avoid !important; }
              }`}</style>
            <div className="leave-statement-table">
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={th} className="name-col">الاسم</th>
                    <th style={th}>الرقم المدني</th>
                    <th style={th}>رقم الملف</th>
                    <th style={th} className="print-hide-column">النوبة</th>
                    <th style={th}>نوع الإجازة</th>
                    <th style={th}>بداية الإجازة</th>
                    <th style={th}>عدد الأيام</th>
                    <th style={th}>نهاية الإجازة</th>
                    <th style={th}>حالة الاعتماد</th>
                    <th style={th} className="print-hide-column">المعتمد بواسطة</th>
                    <th style={th}>تاريخ الاعتماد</th>
                    <th style={th} className="print-only manual-note-col">ملاحظات</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(row => (
                    <tr key={row.id}>
                      <td style={td} className="name-col">{row.fullName}</td>
                      <td style={td}>{row.civilID}</td>
                      <td style={td}>{row.fileNo}</td>
                      <td style={td} className="print-hide-column">{(row.shiftType === 'صباحي' || row.shift === 'M') ? 'صباحي' : row.shift}</td>
                      <td style={td}>{row.leaveTypeArabic || TYPE_LABELS[row.leaveType] || row.leaveType}</td>
                      <td style={td}>{row.startDate}</td>
                      <td style={{ ...td, fontWeight: 'bold' }}>{row.days}</td>
                      <td style={td}>
                        {row.endDate}
                        {row.extended && (
                          <div style={{ fontSize: '11px', color: '#8e44ad', fontWeight: 'bold' }}>
                            مُمدّدة (الأصلي: {row.originalEndDate})
                          </div>
                        )}
                      </td>
                      <td style={{ ...td, fontWeight: 'bold' }}>{approvalStatusLabel(row)}</td>
                      <td style={td} className="print-hide-column">{row.approvedByName || row.extensionApprovedByName || '-'}</td>
                      <td style={td}>{String(row.approvedAt || row.extensionApprovedAt || '').slice(0, 10) || '-'}</td>
                      <td style={td} className="print-only manual-note-col">&nbsp;</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="statement-footer">
                مراقب التشغيل
                <div className="statement-footer-line" />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}


function ReturnStatementTab({ search, setSearch, filters, setFilters, fetchStatement, loading, rows }) {
  return (
    <div>
      <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', marginBottom: '20px' }} className="no-print">
        <h3 style={{ marginTop: 0 }}>بيان مباشرة العمل</h3>
        <p style={{ color: '#666', marginTop: 0 }}>
          هذه الصفحة مخصصة للمباشرات المعتمدة نهائيًا. البحث يكون حسب فترة تاريخ الاعتماد، أو الاسم، أو الرقم المدني، أو رقم الملف، أو نوع الإجازة. جميع الفلاتر اختيارية.
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '15px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '5px' }}>بحث موظف:</label>
            <input type="text" placeholder="اسم / مدني / ملف" value={search} onChange={(e) => setSearch(e.target.value)} style={inputStyle} />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '5px' }}>من تاريخ الاعتماد:</label>
            <input type="date" dir="ltr" lang="en-CA" value={filters.approvalFromDate || ''} onChange={(e) => setFilters({ ...filters, approvalFromDate: e.target.value })} style={dateInputStyle} />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '5px' }}>إلى تاريخ الاعتماد:</label>
            <input type="date" dir="ltr" lang="en-CA" value={filters.approvalToDate || ''} onChange={(e) => setFilters({ ...filters, approvalToDate: e.target.value })} style={dateInputStyle} />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '5px' }}>نوع الإجازة:</label>
            <select value={filters.type} onChange={(e) => setFilters({ ...filters, type: e.target.value })} style={inputStyle}>
              <option value="all">الكل</option>
              {Object.entries(TYPE_LABELS).filter(([key]) => key !== 'returnToWork').map(([key, label]) => <option key={key} value={key}>{label}</option>)}
            </select>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '10px', marginBottom: '15px', flexWrap: 'wrap' }}>
          <button onClick={fetchStatement} style={{ ...btn, backgroundColor: '#27ae60' }}>عرض بيان المباشرة</button>
          <button onClick={() => { setSearch(''); setFilters({ approvalFromDate: '', approvalToDate: '', type: 'all' }); }} style={{ ...btn, backgroundColor: '#7f8c8d' }}>مسح البحث</button>
          <button onClick={() => window.print()} style={{ ...btn, backgroundColor: '#e74c3c' }}>طباعة البيان</button>
        </div>

        <p style={{ color: '#666' }}>كل فلاتر البحث اختيارية. إذا تريد يوم اعتماد واحد، اختر نفس التاريخ في خانتي من/إلى.</p>
      </div>

      <div className="statement-print-shell print-target" style={{ backgroundColor: 'white', padding: '25px', borderRadius: '8px', overflowX: 'auto' }}>
        <OfficialHeader
          title="بيان مباشرة العمل"
          subtitle={(filters.approvalFromDate || filters.approvalToDate) ? `فترة الاعتماد: ${filters.approvalFromDate || 'البداية'} إلى ${filters.approvalToDate || 'النهاية'}` : undefined}
        />
        {loading ? <p style={{ textAlign: 'center', padding: '30px' }}>جاري التحميل...</p> : rows.length === 0 ? (
          <p style={{ textAlign: 'center', color: '#999', padding: '25px' }}>لا توجد بيانات للعرض</p>
        ) : (
          <>
            <style>{`
              .return-statement-table th, .return-statement-table td { border: 1px solid #34495e !important; text-align: center !important; background: #fff !important; font-size: 13px !important; font-weight: 700 !important; }
              .return-statement-table th { color: #0b1f4d; font-weight: 700 !important; font-size: 13.5px !important; }
              .return-statement-table .name-col { width: 1% !important; min-width: 115px !important; white-space: nowrap !important; font-weight: 700 !important; font-size: 13px !important; }
              .return-statement-table .print-only { display: none; }
              .return-statement-table .manual-note-col { min-width: 155px; width: 18%; }
              .statement-footer { margin-top: 35px; width: 220px; float: left; clear: both; text-align: center; color: #0b1f4d; font-weight: 700; font-size: 16px; direction: rtl; }
              .statement-footer-line { border-top: 1.5px solid #111; margin-top: 18px; }
              @media print {
                @page { size: A4 landscape; margin: 8mm; @bottom-center { content: counter(page); font-size: 10px; color: #111; } }
                html, body, #root { margin: 0 !important; padding: 0 !important; height: auto !important; overflow: visible !important; background: #fff !important; }
                .no-print { display: none !important; }
                .print-target { display: block !important; position: static !important; width: 100% !important; margin: 0 !important; padding: 0 !important; border-radius: 0 !important; overflow: visible !important; background: #fff !important; box-shadow: none !important; page-break-after: avoid !important; break-after: avoid-page !important; }
                .return-statement-table .no-print-row-action { display: none !important; }
                .return-statement-table .print-hide-column { display: none !important; }
                .return-statement-table .print-only { display: table-cell !important; }
                .return-statement-table .manual-note-col { min-width: 150px !important; width: 18% !important; height: 30px !important; white-space: normal !important; }
                .return-statement-table table { font-size: 12.5px !important; table-layout: auto !important; width: 100% !important; }
                .return-statement-table .name-col { width: 1% !important; min-width: 115px !important; white-space: nowrap !important; font-weight: 700 !important; font-size: 13px !important; }
                .return-statement-table th, .return-statement-table td { padding: 5px 4px !important; background: #fff !important; color: #0b1f4d !important; border: 1px solid #1d3557 !important; font-size: 13px !important; font-weight: 700 !important; line-height: 1.35 !important; white-space: nowrap !important; }
                .statement-footer { margin-top: 35px !important; float: left !important; clear: both !important; margin-left: 0 !important; margin-right: auto !important; page-break-inside: avoid !important; break-inside: avoid !important; }
              }`}</style>
            <div className="return-statement-table">
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={th} className="name-col">الاسم</th>
                    <th style={th}>الرقم المدني</th>
                    <th style={th}>رقم الملف</th>
                    <th style={th} className="print-hide-column">النوبة</th>
                    <th style={th}>نوع الإجازة</th>
                    <th style={th}>بداية الإجازة</th>
                    <th style={th}>نهاية الإجازة</th>
                    <th style={th}>تاريخ المباشرة</th>
                    <th style={th}>حالة الاعتماد</th>
                    <th style={th} className="print-hide-column">المعتمد بواسطة</th>
                    <th style={th}>تاريخ الاعتماد</th>
                    <th style={th} className="print-only manual-note-col">ملاحظات</th>
                    <th style={th} className="no-print no-print-row-action">إجراء</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(row => (
                    <tr key={row.id}>
                      <td style={td} className="name-col">{row.fullName}</td>
                      <td style={td}>{row.civilID}</td>
                      <td style={td}>{row.fileNo}</td>
                      <td style={td} className="print-hide-column">{(row.shiftType === 'صباحي' || row.shift === 'M') ? 'صباحي' : row.shift}</td>
                      <td style={td}>{row.leaveTypeArabic || TYPE_LABELS[row.leaveType] || row.leaveType || '-'}</td>
                      <td style={td}>{row.startDate || '-'}</td>
                      <td style={td}>{row.endDate || '-'}</td>
                      <td style={{ ...td, fontWeight: 'bold' }}>{row.returnToWorkDate}</td>
                      <td style={{ ...td, fontWeight: 'bold' }}>{approvalStatusLabel(row)}</td>
                      <td style={td} className="print-hide-column">{row.approvedByName || '-'}</td>
                      <td style={td}>{String(row.approvedAt || '').slice(0, 10) || '-'}</td>
                      <td style={td} className="print-only manual-note-col">&nbsp;</td>
                      <td style={td} className="no-print no-print-row-action">
                        <button
                          onClick={() => printOfficialForm('return', { employee: row, leave: row })}
                          style={{ ...btn, backgroundColor: '#16a085', padding: '6px 10px' }}
                        >
                          طباعة مباشرة العمل
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="statement-footer">
                مراقب التشغيل
                <div className="statement-footer-line" />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}


function ArchiveTab({ canRestore, onRestore, onOpen }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await apiFetch(`${API_URL}/employees?status=archived`);
      const data = await res.json();
      if (res.ok) setRows(data.employees || []);
      else alert(data.error || 'تعذر تحميل الأرشيف');
    } catch {
      alert('تعذر الاتصال بالخادم');
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);
  const filtered = rows.filter(emp => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return String(emp.fullName || '').toLowerCase().includes(q) || String(emp.civilID || '').includes(q) || String(emp.fileNo || '').includes(q);
  });

  const restoreAndReload = async (id) => {
    if (onRestore) await onRestore(id);
    await load();
  };

  return (
    <div style={{ backgroundColor: 'white', padding: 25, borderRadius: 8 }}>
      <div className="no-print" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 15, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, color: '#2c3e50' }}>الأرشيف / الموظفون غير النشطين</h2>
        <button onClick={load} style={{ ...btn, backgroundColor: '#3498db' }}>تحديث</button>
      </div>
      <p style={{ color: '#666' }}>الموظف المؤرشف لا يظهر ضمن العدد الحالي ولا في البحث العادي، لكن سجله القديم محفوظ.</p>
      <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="بحث في الأرشيف بالاسم / المدني / الملف" style={inputStyle} />

      {loading ? <p>جاري التحميل...</p> : filtered.length === 0 ? (
        <p style={{ textAlign: 'center', color: '#999', padding: 25 }}>لا توجد بيانات في الأرشيف</p>
      ) : (
        <Table headers={['الاسم', 'رقم الملف', 'الرقم المدني', 'النوبة', 'مكان العمل', 'سبب الإزالة', 'تاريخ الإزالة', 'تمت بواسطة', 'إجراء']}>
          {filtered.map(emp => (
            <tr key={emp.id}>
              <td style={td}>{emp.fullName}</td>
              <td style={td}>{emp.fileNo}</td>
              <td style={td}>{emp.civilID}</td>
              <td style={td}>{emp.shift}</td>
              <td style={td}>{emp.workLocation || '-'}</td>
              <td style={td}>{emp.archiveReason || '-'}</td>
              <td style={td}>{emp.archiveDate || '-'}</td>
              <td style={td}>{emp.archivedByName || '-'}</td>
              <td style={td} className="no-print">
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <button onClick={() => onOpen(emp)} style={{ ...btn, backgroundColor: '#34495e', padding: '6px 12px' }}>فتح السجل</button>
                  {canRestore && <button onClick={() => restoreAndReload(emp.id)} style={{ ...btn, backgroundColor: '#27ae60', padding: '6px 12px' }}>استرجاع</button>}
                </div>
              </td>
            </tr>
          ))}
        </Table>
      )}
    </div>
  );
}


function SystemUpdateTab({ currentUser, systemVersionInfo, onBumped }) {
  const isEnglish = currentUser?.language === 'en';
  const [info, setInfo] = useState(systemVersionInfo || null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const text = {
    title: isEnglish ? 'System Update Control' : 'التحكم بتحديث النظام للمستخدمين',
    description: isEnglish
      ? 'This page does not deploy code. After deploying a new version from Cloud Shell, use this button to show a bilingual update notice to users. Pages will not reload automatically; users update only when they click Update Now.'
      : 'هذه الصفحة لا ترفع الكود. بعد رفع النسخة من Cloud Shell، استخدم الزر لإظهار تنبيه تحديث ثنائي اللغة للمستخدمين. الصفحات لا تعمل إعادة تحميل تلقائية؛ التحديث يتم فقط عند ضغط المستخدم على تحديث الآن.',
    currentClient: isEnglish ? 'Current browser version' : 'نسخة المتصفح الحالية',
    serverVersion: isEnglish ? 'Server version' : 'نسخة السيرفر',
    refreshKey: isEnglish ? 'Update key' : 'مفتاح التحديث',
    updatedBy: isEnglish ? 'Last triggered by' : 'آخر من نفذ التحديث',
    updatedAt: isEnglish ? 'Last update time' : 'وقت آخر تحديث',
    load: isEnglish ? 'Check version' : 'فحص النسخة',
    force: isEnglish ? 'Force update for all users' : 'إجبار تحديث النظام لجميع المستخدمين',
    confirm: isEnglish
      ? 'This will show a bilingual update notice to users. It will not reload pages automatically. Press OK to continue.'
      : 'سيتم إظهار تنبيه تحديث ثنائي اللغة للمستخدمين. لن يتم تحديث الصفحات تلقائيًا. اضغط موافق للمتابعة.',
    done: isEnglish ? 'Update signal sent.' : 'تم إرسال أمر التحديث للمستخدمين.',
    failed: isEnglish ? 'Unable to update system version.' : 'تعذر تحديث نسخة النظام.',
    connection: isEnglish ? 'Cannot connect to server.' : 'تعذر الاتصال بالخادم',
    note: isEnglish
      ? 'The update signal only displays a notice. It does not interrupt active forms, approvals, or data entry. Users press Update Now when ready.'
      : 'إشارة التحديث تعرض تنبيهًا فقط. لا تقطع النماذج أو الاعتمادات أو إدخال البيانات. المستخدم يضغط تحديث الآن عندما يكون جاهزًا.'
  };

  const load = async () => {
    setLoading(true);
    setMessage('');
    try {
      const res = await apiFetch(`${API_URL}/system-version?t=${Date.now()}`);
      const data = await res.json();
      if (res.ok) setInfo(data);
      else setMessage(data.error || text.failed);
    } catch {
      setMessage(text.connection);
    }
    setLoading(false);
  };

  const forceUpdate = async () => {
    if (!window.confirm(text.confirm)) return;
    setLoading(true);
    setMessage('');
    try {
      const res = await apiFetch(`${API_URL}/system-version/bump`, { method: 'POST', body: JSON.stringify({ note: 'manual_force_refresh' }) });
      const data = await res.json();
      if (res.ok) {
        setInfo(data);
        if (data.refreshKey) localStorage.setItem(CLIENT_REFRESH_KEY, String(data.refreshKey));
        if (typeof onBumped === 'function') onBumped(data);
        setMessage(text.done);
      } else {
        setMessage(data.error || text.failed);
      }
    } catch {
      setMessage(text.connection);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  return (
    <div style={{ backgroundColor: 'white', padding: 25, borderRadius: 8 }} dir={isEnglish ? 'ltr' : 'rtl'}>
      <h2 style={{ marginTop: 0 }}>{text.title}</h2>
      <p style={{ color: '#666', lineHeight: 1.8 }}>{text.description}</p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginBottom: 16 }}>
        <div style={{ background: '#f8f9fa', border: '1px solid #ddd', borderRadius: 8, padding: 12 }}><strong>{text.currentClient}:</strong><br />{APP_VERSION}</div>
        <div style={{ background: '#f8f9fa', border: '1px solid #ddd', borderRadius: 8, padding: 12 }}><strong>{text.serverVersion}:</strong><br />{info?.version || '-'}</div>
        <div style={{ background: '#f8f9fa', border: '1px solid #ddd', borderRadius: 8, padding: 12 }}><strong>{text.refreshKey}:</strong><br /><span dir="ltr">{info?.refreshKey || '-'}</span></div>
        <div style={{ background: '#f8f9fa', border: '1px solid #ddd', borderRadius: 8, padding: 12 }}><strong>{text.updatedBy}:</strong><br />{info?.updatedByName || '-'}</div>
        <div style={{ background: '#f8f9fa', border: '1px solid #ddd', borderRadius: 8, padding: 12 }}><strong>{text.updatedAt}:</strong><br /><span dir="ltr">{String(info?.updatedAt || '').replace('T', ' ').slice(0, 19) || '-'}</span></div>
      </div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
        <button onClick={load} disabled={loading} style={{ ...btn, backgroundColor: '#3498db' }}>{text.load}</button>
        <button onClick={forceUpdate} disabled={loading} style={{ ...btn, backgroundColor: '#d35400' }}>{text.force}</button>
      </div>
      {message && <div style={{ color: message.includes('تعذر') || message.includes('Unable') || message.includes('Cannot') ? '#c0392b' : '#27ae60', fontWeight: 'bold', marginBottom: 12 }}>{message}</div>}
      <div style={{ background: '#fff8e1', color: '#7a4f01', border: '1px solid #f1c40f', borderRadius: 8, padding: 12, lineHeight: 1.8 }}>{text.note}</div>
    </div>
  );
}

function AuditLogsTab() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await apiFetch(`${API_URL}/audit-logs?limit=300`);
      const data = await res.json();
      if (res.ok) setLogs(data.logs || []);
      else alert(data.error || 'تعذر تحميل سجل العمليات');
    } catch {
      alert('تعذر الاتصال بالخادم');
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);
  const filtered = logs.filter(log => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return JSON.stringify(log).toLowerCase().includes(q);
  });

  return (
    <div style={{ backgroundColor: 'white', padding: 25, borderRadius: 8 }}>
      <div className="no-print" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 15, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, color: '#2c3e50' }}>سجل العمليات</h2>
        <button onClick={load} style={{ ...btn, backgroundColor: '#3498db' }}>تحديث</button>
      </div>
      <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="بحث في سجل العمليات" style={inputStyle} />
      {loading ? <p>جاري التحميل...</p> : filtered.length === 0 ? (
        <p style={{ textAlign: 'center', color: '#999', padding: 25 }}>لا توجد عمليات مسجلة</p>
      ) : (
        <Table headers={['التاريخ', 'العملية', 'المستخدم', 'الصلاحية', 'النوع', 'المعرف', 'تفاصيل']}>
          {filtered.map(log => (
            <tr key={log.id}>
              <td style={td}>{String(log.createdAt || log.performedAt || '').replace('T', ' ').slice(0, 19)}</td>
              <td style={td}>{AUDIT_ACTION_LABELS[log.action] || log.action || '-'}</td>
              <td style={td}>{log.actorName || log.performedByName || log.userName || '-'}</td>
              <td style={td}>{ROLE_LABELS_SHORT[log.actorRole || log.performedByRole] || log.actorRole || log.performedByRole || '-'}</td>
              <td style={td}>{log.entityType || '-'}</td>
              <td style={td}>{log.entityId || '-'}</td>
              <td style={td}>
                <pre style={{ whiteSpace: 'pre-wrap', margin: 0, fontFamily: 'inherit', fontSize: 12 }}>
                  {JSON.stringify(log.details || log, null, 2).slice(0, 500)}
                </pre>
              </td>
            </tr>
          ))}
        </Table>
      )}
    </div>
  );
}

function LeaveApprovalsTab({ onChanged }) {
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await apiFetch(`${API_URL}/leave-approvals?status=pending`);
      const data = await res.json();
      if (res.ok) setApprovals(data.approvals || []);
      else alert(data.error || 'تعذر تحميل طلبات الاعتماد');
    } catch {
      alert('تعذر الاتصال بالخادم');
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);
  const approve = async (id) => {
    const notes = window.prompt('ملاحظات الاعتماد (اختياري):', '') || '';
    try {
      const res = await apiFetch(`${API_URL}/leave/${id}/approve`, {
        method: 'POST',
        body: JSON.stringify({ notes })
      });
      const data = await res.json();
      if (res.ok) {
        alert(data.message || 'تم الاعتماد');
        await load();
        if (onChanged) onChanged();
      } else {
        alert(data.error || 'تعذر الاعتماد');
      }
    } catch {
      alert('تعذر الاتصال بالخادم');
    }
  };

  const reject = async (id) => {
    const notes = window.prompt('سبب الرفض:', '') || '';
    if (!window.confirm('هل تريد رفض هذا الطلب؟')) return;

    try {
      const res = await apiFetch(`${API_URL}/leave/${id}/reject`, {
        method: 'POST',
        body: JSON.stringify({ notes })
      });
      const data = await res.json();
      if (res.ok) {
        alert(data.message || 'تم الرفض');
        await load();
        if (onChanged) onChanged();
      } else {
        alert(data.error || 'تعذر الرفض');
      }
    } catch {
      alert('تعذر الاتصال بالخادم');
    }
  };

  return (
    <div style={{ backgroundColor: 'white', padding: 25, borderRadius: 8 }}>
      <div className="no-print" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 15 }}>
        <h2 style={{ margin: 0, color: '#2c3e50' }}>اعتماد الإجازات</h2>
        <button onClick={load} style={{ ...btn, backgroundColor: '#3498db' }}>
          تحديث الطلبات
        </button>
      </div>

      <p style={{ color: '#666' }}>
        تظهر هنا الإجازات الدورية والعارضة وطلبات تمديد الإجازة الدورية التي بانتظار اعتماد المراقب.
      </p>

      {loading ? (
        <p>جاري التحميل...</p>
      ) : approvals.length === 0 ? (
        <p style={{ textAlign: 'center', color: '#999', padding: 25 }}>لا توجد طلبات بانتظار الاعتماد</p>
      ) : (
        <Table headers={['الموظف', 'الملف', 'النوبة', 'النوع', 'من', 'إلى', 'مدخل الطلب', 'ملاحظات', 'الإجراء']}>
          {approvals.map(item => (
            <tr key={`${item.id}-${item.approvalKind}`}>
              <td style={td}>{item.employeeName}</td>
              <td style={td}>{item.fileNo}</td>
              <td style={td}>{item.shift}</td>
              <td style={td}>{item.typeArabic}</td>
              <td style={td}>{item.approvalKind === 'extension' ? item.pendingExtension?.currentEndDate : item.startDate}</td>
              <td style={td}>{item.approvalKind === 'extension' ? item.pendingExtension?.requestedNewEndDate : item.endDate}</td>
              <td style={td}>{item.approvalKind === 'extension' ? item.pendingExtension?.requestedByName : item.createdByName || '-'}</td>
              <td style={td}>{item.notes || item.pendingExtension?.notes || '-'}</td>
              <td style={td} className="no-print">
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <button onClick={() => approve(item.id)} style={{ ...btn, backgroundColor: '#27ae60', padding: '6px 12px' }}>
                    اعتماد
                  </button>
                  <button onClick={() => reject(item.id)} style={{ ...btn, backgroundColor: '#e74c3c', padding: '6px 12px' }}>
                    رفض
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </Table>
      )}
    </div>
  );
}


function OfficialHolidaysTab({ version, setVersion }) {
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: '', startDate: '', endDate: '', notes: '' });

  const load = async () => {
    setLoading(true);
    try {
      const res = await apiFetch(`${API_URL}/official-holidays?year=${year}`);
      if (res.ok) {
        const data = await res.json();
        setHolidays(data.holidays || []);
      }
    } catch {
      alert('تعذر تحميل الإجازات الرسمية');
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, [year, version]);

  const save = async (e) => {
    e.preventDefault();
    try {
      const res = await apiFetch(`${API_URL}/official-holidays`, {
        method: 'POST',
        body: JSON.stringify(form)
      });
      const data = await res.json();
      if (res.ok) {
        alert('تم حفظ الإجازة الرسمية.');
        setForm({ name: '', startDate: '', endDate: '', notes: '' });
        setVersion(v => v + 1);
      } else {
        alert(data.error || 'تعذر الحفظ');
      }
    } catch {
      alert('تعذر الاتصال بالخادم');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('هل تريد حذف هذه الإجازة الرسمية؟')) return;
    try {
      const res = await apiFetch(`${API_URL}/official-holidays/${id}`, { method: 'DELETE' });
      if (res.ok) setVersion(v => v + 1);
    } catch {
      alert('تعذر الحذف');
    }
  };

  return (
    <div>
      <div style={{ backgroundColor: 'white', padding: 25, borderRadius: 8, marginBottom: 20 }}>
        <h2 style={{ marginTop: 0, color: '#2c3e50' }}>الإجازات الرسمية للدولة</h2>
        <p style={{ color: '#666' }}>
          هذه الإجازات تخص الدوام الصباحي فقط. النوبات لا تتأثر بالإجازات الرسمية.
        </p>

        <form onSubmit={save} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
          <input placeholder="اسم الإجازة الرسمية" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} style={inputStyle} required />
          <input type="text" placeholder="YYYY-MM-DD" dir="ltr" value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} style={{ ...inputStyle, direction: 'ltr' }} required />
          <input type="date" dir="ltr" lang="en-CA" value={form.endDate} onChange={(e) => setForm({ ...form, endDate: e.target.value })} style={dateInputStyle} />
          <input placeholder="ملاحظات" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} style={inputStyle} />
          <button type="submit" style={{ ...btn, backgroundColor: '#27ae60', height: 42 }}>حفظ الإجازة الرسمية</button>
        </form>
      </div>

      <div style={{ backgroundColor: 'white', padding: 25, borderRadius: 8 }}>
        <div className="no-print" style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 15 }}>
          <label>السنة:</label>
          <input value={year} onChange={(e) => setYear(e.target.value)} style={{ padding: 8, width: 100 }} />
          <button onClick={load} style={{ ...btn, backgroundColor: '#3498db' }}>تحديث</button>
        </div>

        {loading ? <p>جاري التحميل...</p> : (
          <Table headers={['اسم الإجازة', 'من تاريخ', 'إلى تاريخ', 'ملاحظات', 'إجراءات']}>
            {holidays.map(h => (
              <tr key={h.id}>
                <td style={td}>{h.name}</td>
                <td style={td}>{h.startDate}</td>
                <td style={td}>{h.endDate || h.startDate}</td>
                <td style={td}>{h.notes || '-'}</td>
                <td style={td} className="no-print">
                  <button onClick={() => remove(h.id)} style={{ ...btn, backgroundColor: '#e74c3c', padding: '5px 10px' }}>حذف</button>
                </td>
              </tr>
            ))}
          </Table>
        )}
      </div>
    </div>
  );
}

function ShiftSettingsTab({ version, setVersion }) {
  const [systems, setSystems] = useState([]);
  const [checkYear, setCheckYear] = useState(String(new Date().getFullYear()));
  const [check, setCheck] = useState(null);
  const [form, setForm] = useState({
    name: 'نظام النوبات الحالي',
    activeFrom: '2026-01-01',
    cycle: 'M,A,N,O1,O2',
    referenceDate: '2026-01-01',
    A: 'N',
    B: 'A',
    C: 'M',
    D: 'O2',
    F: 'O1'
  });

  const statusOptions = ['M', 'A', 'N', 'O1', 'O2'];

  const load = async () => {
    try {
      const res = await apiFetch(`${API_URL}/shift-systems`);
      if (res.ok) {
        const data = await res.json();
        setSystems(data.systems || []);
      }
    } catch {
      alert('تعذر تحميل إعدادات النوبات');
    }
  };

  useEffect(() => { load(); }, [version]);

  const save = async (e) => {
    e.preventDefault();
    try {
      const body = {
        name: form.name,
        activeFrom: form.activeFrom,
        cycle: form.cycle.split(',').map(x => x.trim()).filter(Boolean),
        referenceDate: form.referenceDate,
        reference: {
          A: form.A,
          B: form.B,
          C: form.C,
          D: form.D,
          F: form.F
        }
      };

      const res = await apiFetch(`${API_URL}/shift-systems`, {
        method: 'POST',
        body: JSON.stringify(body)
      });
      const data = await res.json();

      if (res.ok) {
        alert('تم حفظ نظام النوبات.');
        setVersion(v => v + 1);
      } else {
        alert(data.error || 'تعذر الحفظ');
      }
    } catch {
      alert('تعذر الاتصال بالخادم');
    }
  };

  const runCheck = async () => {
    try {
      const res = await apiFetch(`${API_URL}/calculation-check?year=${checkYear}`);
      if (res.ok) {
        const data = await res.json();
        setCheck(data);
      }
    } catch {
      alert('تعذر فحص الحسابات');
    }
  };

  return (
    <div>
      <div style={{ backgroundColor: 'white', padding: 25, borderRadius: 8, marginBottom: 20 }}>
        <h2 style={{ marginTop: 0, color: '#2c3e50' }}>إعدادات نظام النوبات</h2>
        <p style={{ color: '#666' }}>
          النظام يولد جدول النوبات تلقائيًا حسب الدورة والتاريخ المرجعي. إذا تغير نظام الدوام مستقبلًا، أضف نظامًا جديدًا بتاريخ بداية التطبيق.
        </p>

        <form onSubmit={save}>
          <input placeholder="اسم النظام" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} style={inputStyle} />
          <label>تاريخ بداية تطبيق النظام:</label>
          <input type="date" dir="ltr" lang="en-CA" value={form.activeFrom} onChange={(e) => setForm({ ...form, activeFrom: e.target.value })} style={dateInputStyle} required />
          <label>دورة النوبة:</label>
          <input value={form.cycle} onChange={(e) => setForm({ ...form, cycle: e.target.value })} style={inputStyle} />
          <label>التاريخ المرجعي:</label>
          <input type="date" dir="ltr" lang="en-CA" value={form.referenceDate} onChange={(e) => setForm({ ...form, referenceDate: e.target.value })} style={dateInputStyle} required />

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 10 }}>
            {['A', 'B', 'C', 'D', 'F'].map(sh => (
              <div key={sh}>
                <label>نوبة {sh}:</label>
                <select value={form[sh]} onChange={(e) => setForm({ ...form, [sh]: e.target.value })} style={inputStyle}>
                  {statusOptions.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            ))}
          </div>

          <button type="submit" style={{ ...btn, backgroundColor: '#27ae60' }}>حفظ نظام النوبات</button>
        </form>
      </div>

      <div style={{ backgroundColor: 'white', padding: 25, borderRadius: 8, marginBottom: 20 }}>
        <h3 style={{ marginTop: 0 }}>فحص الحسابات</h3>
        <div className="no-print" style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 15 }}>
          <label>السنة:</label>
          <input value={checkYear} onChange={(e) => setCheckYear(e.target.value)} style={{ padding: 8, width: 100 }} />
          <button onClick={runCheck} style={{ ...btn, backgroundColor: '#3498db' }}>فحص</button>
        </div>

        {check && (() => {
          const shiftNames = ['A', 'B', 'C', 'D', 'F'];
          const sampleDates = check.shifts?.A?.sample?.map(x => x.date) || [];
          const getSampleStatus = (shift, date) => {
            const found = check.shifts?.[shift]?.sample?.find(x => x.date === date);
            return found ? `${found.status} - ${found.statusArabic}` : '-';
          };

          return (
            <div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                gap: 12,
                marginBottom: 18
              }}>
                <div style={{ backgroundColor: '#f8f9fa', padding: 15, borderRadius: 8 }}>
                  <div style={{ color: '#666', fontSize: 13 }}>أيام عمل الصباحي بعد خصم الجمعة والسبت والإجازات الرسمية</div>
                  <div style={{ fontSize: 26, fontWeight: 'bold', color: '#2c3e50' }}>{check.morningWorkDays}</div>
                </div>

                <div style={{ backgroundColor: '#f8f9fa', padding: 15, borderRadius: 8 }}>
                  <div style={{ color: '#666', fontSize: 13 }}>الإجازات الرسمية المحتسبة على الصباحي</div>
                  <div style={{ fontSize: 26, fontWeight: 'bold', color: '#2c3e50' }}>{check.officialHolidayDaysForMorning}</div>
                </div>
              </div>

              <h4 style={{ marginBottom: 10 }}>أيام العمل السنوية لكل نوبة</h4>
              <Table headers={['النوبة', 'أيام العمل السنوية']}>
                {Object.entries(check.shifts || {}).map(([sh, data]) => (
                  <tr key={sh}>
                    <td style={{ ...td, fontWeight: 'bold' }}>{sh}</td>
                    <td style={{ ...td, fontWeight: 'bold', fontSize: 18 }}>{data.workDays}</td>
                  </tr>
                ))}
              </Table>

              <h4 style={{ margin: '25px 0 10px 0' }}>عينة أول 5 أيام من السنة</h4>
              <Table headers={['التاريخ', ...shiftNames.map(sh => `نوبة ${sh}`)]}>
                {sampleDates.map(date => (
                  <tr key={date}>
                    <td style={{ ...td, direction: 'ltr', textAlign: 'center', fontWeight: 'bold' }}>{date}</td>
                    {shiftNames.map(sh => (
                      <td key={sh} style={{ ...td, textAlign: 'center', fontWeight: 'bold' }}>
                        {getSampleStatus(sh, date)}
                      </td>
                    ))}
                  </tr>
                ))}
              </Table>
            </div>
          );
        })()}
      </div>

      <div style={{ backgroundColor: 'white', padding: 25, borderRadius: 8 }}>
        <h3 style={{ marginTop: 0 }}>الأنظمة المحفوظة</h3>
        <Table headers={['الاسم', 'يبدأ من', 'الدورة', 'التاريخ المرجعي', 'مرجع النوبات']}>
          {systems.map((s, idx) => (
            <tr key={s.id || idx}>
              <td style={td}>{s.name}</td>
              <td style={td}>{s.activeFrom}</td>
              <td style={td}>{Array.isArray(s.cycle) ? s.cycle.join(' → ') : '-'}</td>
              <td style={td}>{s.referenceDate}</td>
              <td style={td}>
                A={s.reference?.A} | B={s.reference?.B} | C={s.reference?.C} | D={s.reference?.D} | F={s.reference?.F}
              </td>
            </tr>
          ))}
        </Table>
      </div>
    </div>
  );
}
